import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Center,
  Text,
  Flex,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  useDisclosure,
  useToast,
  Checkbox,
  VStack,
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  HStack,
  Tooltip,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  IconButton,
  Switch,
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  MenuDivider,
  Portal,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
  getSupabase,
  getSupabaseRole,
  supabasePublic,
  supabaseSchema,
} from "../components/supabaseClient";
import HipaaComplianceModal from "./superadmin/HipaaComplianceModal";
import { isCutoverEffective, startAdminPatientsFromRds, serveAdminPatientsFromRds, mirrorWriteToRds } from "../utils/rdsCutover";
import LiveActivityModal from "./superadmin/LiveActivityModal";
import SmsSpendModal from "./superadmin/SmsSpendModal";
import AgentGapsModal from "./superadmin/AgentGapsModal";
import AgentPermissionsModal from "../components/agents/AgentPermissionsModal";
import MigrationModal from "./superadmin/MigrationModal";
import { ActionMenu } from "../components/admin/ActionMenu";
import { isImpersonating } from "../utils/impersonation";
import { normalizeRole, assignableSignupRoles, isSuperAdminRole } from "../config/roles";
import {
  parseSchemaList,
  setNativeSchema,
  addDraftSchema,
  removeDraftSchema,
  tenantSchemaMismatch,
} from "../utils/userAccess";
import SchedulesTable from "../components/SchedulesTable";
import AddPatientModal from "../components/AddPatientModal";
import EditPatientModal from "../components/EditPatientModal";
import FormSelectionModal from "../components/FormSelectionModal";
import { getSendFormsUrl, colid2, basename, getBackendURL } from "../components/env";
import { exposeSchema } from "../utils/exposeSchema";
import { ADMIN_PASSWORD } from "../components/env";
import { logDiagnosticsReport } from "../utils/patientSignupDebug";
import { handleFormSending } from "../utils/formUtils";
import { confirmBadNumbers } from "../utils/badNumbers";
import PhoneStatusBadge from "../components/sms/PhoneStatusBadge";
import {
  findPatientField,
  normalizeFormTypes,
  createFormUrls,
} from "../utils/fuzzyFormMapping";
import { extractAllSubmissionLinks } from "../utils/submissionUtils";
import CreateSchemaModal from "../components/CreateSchemaModal";
import CreditLimitManager from "../components/CreditLimitManager";
import TranscriptionLogs from "../components/TranscriptionLogs";
import SavedTranscriptCredits from "../components/SavedTranscriptCredits";
import BackupPathMigration from "../components/BackupPathMigration";
import AppointmentVersionSettings from "../components/AppointmentVersionSettings";
import AudioPeriodicBackupAdminSettings from "../components/AudioPeriodicBackupAdminSettings";
import ComplianceAdminSettings from "../components/ComplianceAdminSettings";
import SchemaGroupAdminSettings from "../components/SchemaGroupAdminSettings";
import {
  groupChoiceValue,
  resolveApprovalSelection,
  groupForSchema,
  groupsFromRows,
  parseGroupChoice,
  rowsToSchemaGroups,
  schemaOptionsFromRows,
} from "../utils/schemaGroups";
import UserAnalyticsTab from "../components/analytics/UserAnalyticsTab";
import UserAccessTab from "../components/admin/UserAccessTab";
import OrderTemplatesModal from "../components/admin/OrderTemplatesModal";
import BulkJobProgressStrip from "../components/BulkJobProgressStrip";
import ZohoBlockModal from "../components/ZohoBlockModal";
import MeetingAvailabilityModal from "../components/MeetingAvailabilityModal";
import PatientImportModal from "../components/PatientImportModal";
import PatientTableViewer from "../components/PatientTableViewer";
import { invalidateAnalyticsCache } from "../utils/analyticsCache";
import { flushUserEvents, forceHeartbeat } from "../utils/userEventTracker";
import { UserListBulkAddBar } from "../userLists";
import {
  PATIENT_TABLE_CONFIRMATION,
  PATIENT_TABLE_EMPTY_CONFIRMATION,
  PATIENT_TABLE_PROGRESS_MESSAGES,
  runPatientTableJob,
} from "../utils/patientTableJob";
import StorageModal from "./superadmin/StorageModal";
import E2ETestsModal from "./superadmin/E2ETestsModal";
import AudioDayManagerModal from "./superadmin/AudioDayManagerModal";

const READ_ONLY_HINT = "Read-only while viewing as another user";

// Form URLs - must always come from schema_settings table
// Requires userSettings to be passed as parameter
const getFormUrls = (userSettings = {}) => {
  return {
    NP_FORM_URL: userSettings.NP_FORM_URL || "",
    NS_FORM_URL: userSettings.NS_FORM_URL || "",
    CL1_FORM_URL: userSettings.CL1_FORM_URL || "",
    CL2_FORM_URL: userSettings.CL2_FORM_URL || "",
  };
};

// Check if a specific form URL is configured - returns the URL or null if not configured
const getFormUrlForType = (userSettings = {}, formType) => {
  const urls = getFormUrls(userSettings);
  if (formType.includes("NP")) return urls.NP_FORM_URL || null;
  if (formType.includes("NS")) return urls.NS_FORM_URL || null;
  if (formType.includes("CL1")) return urls.CL1_FORM_URL || null;
  if (formType.includes("CL2")) return urls.CL2_FORM_URL || null;
  return null;
};

const SuperAdmin = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [patients, setPatients] = useState([]);
  const [isAddPatientModalOpen, setIsAddPatientModalOpen] = useState(false);
  const [isMeetingAvailabilityOpen, setIsMeetingAvailabilityOpen] = useState(false);
  const [isEditPatientModalOpen, setIsEditPatientModalOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const {
    isOpen: isSendFormOpen,
    onOpen: onSendFormOpen,
    onClose: onSendFormClose,
  } = useDisclosure();
  const {
    isOpen: isPasswordOpen,
    onOpen: onPasswordOpen,
    onClose: onPasswordClose,
  } = useDisclosure();
  const [email, setEmail] = useState("");
  const [formType, setFormType] = useState("");
  const [password, setPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [contactMethods, setContactMethods] = useState({
    email: false,
    phone: false,
  });
  const [phone, setPhone] = useState("");
  const [isSendingForms, setIsSendingForms] = useState(false);
  const {
    isOpen: isSettingsOpen,
    onOpen: onSettingsOpen,
    onClose: onSettingsClose,
  } = useDisclosure();
  const [userSettings, setUserSettings] = useState({});
  const [isAuthorized, setIsAuthorized] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const {
    isOpen: isCreateSchemaOpen,
    onOpen: onCreateSchemaOpen,
    onClose: onCreateSchemaClose,
  } = useDisclosure();
  const {
    isOpen: isHipaaOpen,
    onOpen: onHipaaOpen,
    onClose: onHipaaClose,
  } = useDisclosure();
  const {
    isOpen: isLiveActivityOpen,
    onOpen: onLiveActivityOpen,
    onClose: onLiveActivityClose,
  } = useDisclosure();
  const { isOpen: isSmsSpendOpen, onOpen: onSmsSpendOpen, onClose: onSmsSpendClose } = useDisclosure();
  const { isOpen: isAgentGapsOpen, onOpen: onAgentGapsOpen, onClose: onAgentGapsClose } = useDisclosure();
  const { isOpen: isAgentPermsOpen, onOpen: onAgentPermsOpen, onClose: onAgentPermsClose } = useDisclosure();
  const {
    isOpen: isMigrationOpen,
    onOpen: onMigrationOpen,
    onClose: onMigrationClose,
  } = useDisclosure();
  const {
    isOpen: isStorageOpen,
    onOpen: onStorageOpen,
    onClose: onStorageClose,
  } = useDisclosure();
  const {
    isOpen: isE2EOpen,
    onOpen: onE2EOpen,
    onClose: onE2EClose,
  } = useDisclosure();
  const {
    isOpen: isAudioManagerOpen,
    onOpen: onAudioManagerOpen,
    onClose: onAudioManagerClose,
  } = useDisclosure();
  const {
    isOpen: isCreditManagementOpen,
    onOpen: onCreditManagementOpen,
    onClose: onCreditManagementClose,
  } = useDisclosure();
  const {
    isOpen: isVersionTimingOpen,
    onOpen: onVersionTimingOpen,
    onClose: onVersionTimingClose,
  } = useDisclosure();
  const {
    isOpen: isAudioBackupSettingsOpen,
    onOpen: onAudioBackupSettingsOpen,
    onClose: onAudioBackupSettingsClose,
  } = useDisclosure();
  const {
    isOpen: isComplianceSettingsOpen,
    onOpen: onComplianceSettingsOpen,
    onClose: onComplianceSettingsClose,
  } = useDisclosure();
  const {
    isOpen: isSchemaGroupsOpen,
    onOpen: onSchemaGroupsOpen,
    onClose: onSchemaGroupsClose,
  } = useDisclosure();
  // [{ schema_name, group }] from public.schema_settings.
  const [schemaGroupRows, setSchemaGroupRows] = useState([]);
  const {
    isOpen: isSignupsDashboardOpen,
    onOpen: onSignupsDashboardOpen,
    onClose: onSignupsDashboardClose,
  } = useDisclosure();
  const {
    isOpen: isFaxNumbersOpen,
    onOpen: onFaxNumbersOpen,
    onClose: onFaxNumbersClose,
  } = useDisclosure();
  // [{ schema_name, fax_num, access_id, has_password, new_password }]
  const [faxSchemas, setFaxSchemas] = useState([]);
  const [faxNumbersLoading, setFaxNumbersLoading] = useState(false);
  const [savingAllFax, setSavingAllFax] = useState(false); // saving the whole Fax Numbers modal

  // Master switch: when off, all outbound messages are blocked for supabaseSchema
  const [messagesEnabled, setMessagesEnabled] = useState(true);
  const [loadingMessagesEnabledSetting, setLoadingMessagesEnabledSetting] =
    useState(false);

  const [currentUserName, setCurrentUserName] = useState("");
  const [viewerTimezone, setViewerTimezone] = useState(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Los_Angeles"; }
    catch { return "America/Los_Angeles"; }
  });

  // Order Form Templates — the blank forms doctors fill when they generate an
  // order. Shared per schema group, so they are managed here rather than
  // per-clinic.
  const {
    isOpen: isOrderTemplatesOpen,
    onOpen: onOrderTemplatesOpen,
    onClose: onOrderTemplatesClose,
  } = useDisclosure();
  const [signupUsers, setSignupUsers] = useState([]);
  const [signupPeriod, setSignupPeriod] = useState("week");
  const [signupStatus, setSignupStatus] = useState("all");
  const [signupRoleFilter, setSignupRoleFilter] = useState("all");
  const [signupsLoading, setSignupsLoading] = useState(false);
  const [approvingUserId, setApprovingUserId] = useState(null);
  // "Edit access" row action: the signup row being edited plus the schema
  // draft (list with the native schema first, and the two text inputs).
  const [schemaTargetUser, setSchemaTargetUser] = useState(null);
  const [accessDraft, setAccessDraft] = useState({ nativeInput: "", base: [], extraInput: "" });
  const [savingAccess, setSavingAccess] = useState(false);
  // Row whose inline role dropdown (Status column) is being saved.
  const [roleSavingUserId, setRoleSavingUserId] = useState(null);
  const [selectedSignupIds, setSelectedSignupIds] = useState(() => new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(null); // "welcome" | "approval" | null
  const [activeJob, setActiveJob] = useState(null);     // bulk job record from backend
  const [blockEmail, setBlockEmail] = useState(null);   // dict or null
  const [lastBlockEmail, setLastBlockEmail] = useState(null); // remembered even when modal is suppressed
  const [zohoCheckLoading, setZohoCheckLoading] = useState(false);
  const [analyticsRefreshNonce, setAnalyticsRefreshNonce] = useState(0);
  const [signupsActiveTab, setSignupsActiveTab] = useState(0); // 0=Signups, 1=Referral, 2=User Analytics, 3=Patient Signups, 4=User Access
  const [patientSignupStatusFilter, setPatientSignupStatusFilter] = useState("needs_review");
  // Plain-English "why is signup broken on this server" report from
  // /api/admin/patient-signup-diagnostics/ (missing env vars, migrations, etc.).
  const [signupHealth, setSignupHealth] = useState(null);
  const [signupHealthLoading, setSignupHealthLoading] = useState(false);
  // Editable "confirm the match" inputs per patient signup: { [userId]: { schema, rowId } }.
  const [patientApprovalDrafts, setPatientApprovalDrafts] = useState({});
  // In-flight approve/deny action key: `${userId}:approve` | `${userId}:deny` | null.
  const [patientActionLoading, setPatientActionLoading] = useState(null);
  const [analyticsInitialUserId, setAnalyticsInitialUserId] = useState(null);
  const [analyticsInitialTimeWindow, setAnalyticsInitialTimeWindow] = useState("7d");
  const [isPatientTableConfirmOpen, setIsPatientTableConfirmOpen] = useState(false);
  const [isBuildingPatientTable, setIsBuildingPatientTable] = useState(false);
  // Destructive opt-in: wipe all rows and rebuild the patient tables from notes-c.
  const [dropAndRecreate, setDropAndRecreate] = useState(false);
  const [patientTableProgressIndex, setPatientTableProgressIndex] = useState(0);
  const [isPatientImportOpen, setIsPatientImportOpen] = useState(false);
  const [patientTableTab, setPatientTableTab] = useState(0); // 0=Build/import, 1=View table

  const closePatientTableConfirm = () => {
    if (!isBuildingPatientTable) {
      setIsPatientTableConfirmOpen(false);
      setDropAndRecreate(false);
    }
  };

  // empty=true creates the table structure (+ RLS/grants) only, skipping the
  // notes-c scan and inserts — for starting a schema's patient table from scratch.
  const handleBuildPatientTable = async (empty = false) => {
    setPatientTableProgressIndex(0);
    setIsBuildingPatientTable(true);
    try {
      const result = await runPatientTableJob({
        backendUrl: getBackendURL(),
        schema: supabaseSchema || "public",
        empty,
        reset: dropAndRecreate,
      });
      // The build also applies the canonical patients_with_ehrid migration.
      // When that part fails the tables still exist but are NOT fully set up —
      // the VitaID trigger would reference a function that was never created —
      // so say so instead of reporting a clean success.
      const migration = result?.migration;
      const migrationFailed = migration && migration.applied === false;
      if (migrationFailed) {
        toast({
          title: "Tables built, but not fully migrated",
          description:
            `patients_with_ehrid exists in ${supabaseSchema || "public"}, but the ` +
            `setup migration didn't finish (${migration.reason || "unknown reason"}). ` +
            `Run sql/migrations/2026-07-30-patients-with-ehrid-full.sql by hand ` +
            `before relying on VitaIDs.`,
          status: "warning",
          duration: 15000,
          isClosable: true,
        });
      } else if (empty) {
        toast({
          title: "Empty patient tables created",
          description: `patients_with_ehrid and patients_no_ehrid are ready in ${supabaseSchema || "public"} (no data imported). You can now import a CSV.`,
          status: "success",
          duration: 7000,
          isClosable: true,
        });
      } else {
        const processed = result?.summary?.total_visit_rows_processed ?? 0;
        const withEhrId = result?.inserted?.patients_with_ehrid ?? 0;
        const withoutEhrId = result?.inserted?.patients_no_ehrid ?? 0;
        toast({
          title: "Patient table built",
          description: `Processed ${processed} visits. Inserted ${withEhrId} EHR patients and ${withoutEhrId} non-EHR patients.`,
          status: "success",
          duration: 7000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: empty ? "Create empty tables failed" : "Patient table failed",
        description: error.message || "Failed to build patient table",
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setIsBuildingPatientTable(false);
      setIsPatientTableConfirmOpen(false);
    }
  };

  const refreshAnalyticsData = async () => {
    try { forceHeartbeat(); } catch {}
    try { await flushUserEvents(); } catch {}
    invalidateAnalyticsCache();
    setAnalyticsRefreshNonce((n) => n + 1);
  };

  // Clinic groups for the approval dropdown + the "clinic" badge. Failure is
  // non-fatal on purpose: an admin whose database predates the "Group" column
  // still gets the plain per-schema dropdown rather than a broken dashboard.
  const loadSchemaGroups = useCallback(async () => {
    try {
      const { data, error } = await supabasePublic()
        .from("schema_settings")
        .select("*")
        .order("schema_name", { ascending: true });
      if (error) throw error;
      setSchemaGroupRows(rowsToSchemaGroups(data));
    } catch (err) {
      console.warn("[signups-dashboard] could not load schema groups:", err.message);
      setSchemaGroupRows([]);
    }
  }, []);

  const openSignupsDashboard = async () => {
    await refreshAnalyticsData();
    onSignupsDashboardOpen();
    fetchSignupUsers();
    fetchActiveBulkJob();
    fetchZohoBlockStatus({ force: false, announce: false });
  };

  // The Confirm-schema dropdown is now sourced ENTIRELY from schema_settings,
  // so this has to run on every path that shows the dashboard — including the
  // impersonation-return path, which re-opens the modal from sessionStorage
  // without going through openSignupsDashboard.
  useEffect(() => {
    if (isSignupsDashboardOpen) loadSchemaGroups();
  }, [isSignupsDashboardOpen, loadSchemaGroups]);

  useEffect(() => {
    // If we just exited a view-as session, the impersonation module wrote
    // the modal state to sessionStorage before navigating us here. Re-open
    // the dashboard with the same tab + selected user.
    let raw;
    try { raw = sessionStorage.getItem("imp:return_pending"); } catch (_) { raw = null; }
    if (!raw) return;
    try { sessionStorage.removeItem("imp:return_pending"); } catch (_) {}
    let st = {};
    try { st = JSON.parse(raw) || {}; } catch (_) { st = {}; }
    if (!st.signupsOpen) return;
    if (st.activeTab === "user-analytics") setSignupsActiveTab(2);
    if (st.selectedUserId) setAnalyticsInitialUserId(st.selectedUserId);
    if (st.timeWindow) setAnalyticsInitialTimeWindow(st.timeWindow);
    // Open in a microtask so other init effects settle first.
    Promise.resolve().then(() => onSignupsDashboardOpen());
    fetchSignupUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const checkUserRole = async () => {
      const supabase = await getSupabase();
      try {
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();
        if (sessionError || !session) {
          toast({
            title: "Authentication Error",
            description: "Please log in to access this page.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          navigate("/login");
          return;
        }

        const userEmail = session.user.email;
        const { data, error } = await supabase
          .from("users")
          .select("role, name, timezone")
          .eq("email", userEmail)
          .single();

        console.log("data of user", data);

        if (error) throw error;

        setCurrentUserName(data.name || session.user.user_metadata?.full_name || userEmail.split("@")[0]);
        if (data.timezone) setViewerTimezone(data.timezone);

        if (normalizeRole(data.role) === "admin") {
          setIsAdmin(true);
          setIsAuthenticated(true);
          localStorage.setItem("isAdminAuthenticated", "true");
        } else if (["doctor", "superadmin"].includes(normalizeRole(data.role))) {
          setIsAdmin(false);
          setIsAuthenticated(true); // Directly authenticate doctors (and superadmins) without password
          // TODO: Uncomment below to restore password check for doctors
          // if (localStorage.getItem('isAdminAuthenticated') !== 'true') {
          //   onPasswordOpen();
          // } else {
          //   setIsAuthenticated(true);
          // }
        }
      } catch (error) {
        console.error("Error checking user role:", error.message);
        toast({
          title: "Error",
          description: "Failed to verify user role.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        navigate("/login");
      }
    };

    const fetchPatients = async () => {
      const supabase = await getSupabase();
      try {
        // Phase 4 cutover, site 11 (utils/rdsCutover.js `admin_patients`):
        // the schema-bound client reads the viewer's own tenant, so RDS is
        // asked for that tenant's `patients` alongside it; Supabase's rows
        // stay the oracle and the fallback. Off = the old behaviour.
        // The flag is looked up alongside the select rather than before it:
        // the Supabase request no longer queues behind a cold GET /cutover
        // (up to its 2.5 s timeout), and RDS is still asked as soon as the
        // flag answers.
        const adminSchema = supabaseSchema || "public";
        let rdsStarted = null;
        const flagChecked = isCutoverEffective("admin_patients").then((adminCutover) => {
          if (adminCutover) rdsStarted = startAdminPatientsFromRds(adminSchema);
        });
        const { data, error } = await supabase
          .from("patients")
          .select("*"); // "*" already includes forms_ind where the column exists; naming it broke schemas without it
        if (error) throw error;
        let rows = Array.isArray(data) ? data : [];
        await flagChecked;
        if (rdsStarted) {
          const served = await serveAdminPatientsFromRds(adminSchema, { supabaseRows: rows, started: rdsStarted });
          rows = served.rows;
        }
        setPatients(rows);
      } catch (error) {
        console.error("Error fetching patients:", error.message);
      }
    };

    checkUserRole();
    fetchPatients();
  }, [navigate, toast]);

  const parseSignupReason = (signupReason) => {
    if (!signupReason) return { reason: "", referralSource: "", referralDetail: "" };
    const parts = signupReason.split(" ||| ");
    const reason = parts[0] || "";
    const referralRaw = parts[1] || "";
    const colonIdx = referralRaw.indexOf(": ");
    if (colonIdx !== -1) {
      return { reason, referralSource: referralRaw.substring(0, colonIdx), referralDetail: referralRaw.substring(colonIdx + 2) };
    }
    return { reason, referralSource: referralRaw, referralDetail: "" };
  };

  const formatSignupTime = (iso, tz) => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    const zone = tz || "America/Los_Angeles";
    let datePart, timePart;
    try {
      datePart = d.toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric", timeZone: zone,
      });
      timePart = d.toLocaleTimeString("en-US", {
        hour: "numeric", minute: "2-digit", timeZone: zone, timeZoneName: "short",
      });
    } catch {
      datePart = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      timePart = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZoneName: "short" });
    }
    const tooltipLabel = `Shown in your timezone: ${zone}`;
    return (
      <Tooltip label={tooltipLabel} hasArrow placement="top">
        <Box cursor="help" lineHeight="1.25">
          <Text fontSize="xs">{datePart}</Text>
          <Text fontSize="xs" color="gray.500">{timePart}</Text>
        </Box>
      </Tooltip>
    );
  };

  const fetchSignupUsers = async () => {
    setSignupsLoading(true);
    try {
      const backendUrl = getBackendURL();
      console.log("[signups-dashboard] Fetching from backend:", `${backendUrl}/api/admin/signups/`);
      const res = await fetch(`${backendUrl}/api/admin/signups/`);
      const json = await res.json();
      console.log("[signups-dashboard] Response:", { userCount: json.users?.length, error: json.error });
      if (json.error) throw new Error(json.error);
      setSignupUsers(json.users || []);
    } catch (err) {
      console.error("[signups-dashboard] fetchSignupUsers error:", err);
      toast({ title: "Failed to load signups", description: err.message, status: "error", duration: 4000 });
    } finally {
      setSignupsLoading(false);
    }
  };

  const normalizedRole = (role) => (role || "").trim().toLowerCase();
  const isPending = (role) => {
    const r = normalizedRole(role);
    // Any comma-prefixed role is an unapproved signup — "user,X" (awaiting admin
    // approval) AND "unauthenticated,X" (e.g. a patient signup pending review).
    // Previously this only matched "user,…", so patients showed "PENDING
    // (PATIENT)" in the status badge yet were excluded from the Pending filter
    // (and mislabeled "Approved" in CSV export). Approved roles are single
    // tokens with no comma. Matches getRoleBadge's pending rule.
    return !r || r === "user" || r.includes(",");
  };
  const isPendingApprovable = (role) => normalizedRole(role).startsWith("user,");
  const getApprovedRole = (role) => {
    const raw = (role || "").trim();
    return raw.toLowerCase().startsWith("user,") ? raw.split(",")[1]?.trim() || null : null;
  };
  const signupRoleTokens = (role) => {
    const raw = (role || "").trim();
    if (!raw) return [];
    return raw
      .split(",")
      .map((token) => token.trim().toLowerCase())
      .filter(Boolean);
  };

  // Defined here (rather than beside the other patient helpers below) because
  // the signupAvailableRoles useMemo runs during the component body and would
  // hit the temporal dead zone if this were declared after it.
  const isPatientSignupRow = (u) => {
    const tokens = signupRoleTokens(u.role);
    const hasPatientRole = tokens.length > 0 && tokens[tokens.length - 1] === "patient";
    const hasPatientField = [
      u.patient_first_name,
      u.patient_middle_name,
      u.patient_last_name,
      u.patient_full_name,
      u.patient_dob,
      u.patient_phone,
      u.patient_email,
    ].some((value) => typeof value === "string" && value.trim());
    return hasPatientRole || hasPatientField;
  };

  // Only roles that can actually appear in the Signups tab. Patient rows are
  // filtered out of that tab, so offering "patient" here would be a dead option
  // that always renders "No signups in this period".
  const signupAvailableRoles = useMemo(() => {
    const seen = new Set();
    signupUsers.forEach((u) => {
      if (isPatientSignupRow(u)) return;
      signupRoleTokens(u.role).forEach((token) => seen.add(token));
    });
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signupUsers]);

  const getFilteredSignups = () => {
    const now = new Date();
    return signupUsers.filter((u) => {
      // Patient signups belong exclusively to the "Patient Signups" tab, which
      // has its own approve/deny workflow — they must never show up here. We
      // exclude everything isPatientSignupRow matches, including orphaned
      // "…,patient" role rows carrying no patient data: those aren't staff
      // signups either, and there is no action to take on them in this tab.
      // Filtering at the source keeps the "N users" count, the select-all
      // checkbox, the CSV export and the bulk email actions all consistent —
      // every one of them derives from this list.
      // Only REAL patient signups move to the Patient Signups tab. A row whose
      // role merely ends in "patient" but carries no patient data is an orphaned
      // role assignment — the Patient Signups tab filters those out too, so
      // excluding them here as well would make them invisible everywhere. They
      // stay listed here so they remain discoverable.
      if (isPatientSignupRow(u) && hasPatientSignupData(u)) return false;
      // Period filter
      if (u.created_at) {
        const created = new Date(u.created_at);
        if (signupPeriod === "today" && created.toDateString() !== now.toDateString()) return false;
        if (signupPeriod === "week") { const c = new Date(now); c.setDate(now.getDate() - 7); if (created < c) return false; }
        if (signupPeriod === "month") { const c = new Date(now); c.setDate(now.getDate() - 30); if (created < c) return false; }
      } else if (signupPeriod !== "all") {
        return false;
      }
      // Status filter
      if (signupStatus === "pending") return isPending(u.role);
      if (signupStatus === "accepted") return !isPending(u.role);
      if (signupRoleFilter !== "all") {
        const tokens = signupRoleTokens(u.role);
        if (!tokens.includes(signupRoleFilter)) return false;
      }
      return true;
    });
  };

  // A row is a real patient *signup submission* only if it carries submitted
  // patient data or match metadata. Some rows merely have a role ending in
  // "patient" (e.g. status not_started/denied with nothing attached) — those
  // are orphaned role assignments, not signups, and rendered as blank
  // "Needs review" cards with every field "—". Require actual data to show.
  const hasPatientSignupData = (u) => {
    const hasField = [
      u.patient_first_name,
      u.patient_middle_name,
      u.patient_last_name,
      u.patient_full_name,
      u.patient_dob,
      u.patient_phone,
      u.patient_email,
    ].some((value) => typeof value === "string" && value.trim());
    const meta = patientMatchMetadata(u);
    const hasMeta = meta && Object.keys(meta).length > 0;
    return hasField || hasMeta;
  };

  const patientDisplayValue = (value) => {
    if (typeof value === "string") return value.trim() || "—";
    if (typeof value === "number") return Number.isFinite(value) ? String(value) : "—";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return "—";
  };

  const firstPatientDisplayValue = (...values) => {
    const displayValue = values.find((value) => (
      (typeof value === "string" && value.trim()) ||
      (typeof value === "number" && Number.isFinite(value)) ||
      typeof value === "boolean"
    ));
    return patientDisplayValue(displayValue);
  };

  const patientObjectValue = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    return value;
  };

  const patientJoinedName = (...values) => {
    const parts = values
      .filter((value) => typeof value === "string" && value.trim())
      .map((value) => value.trim());
    return parts.length ? parts.join(" ") : "";
  };

  const patientLastFirstName = (last, first, middle) => {
    const givenName = patientJoinedName(first, middle);
    return [last, givenName]
      .filter((value) => typeof value === "string" && value.trim())
      .map((value) => value.trim())
      .join(", ");
  };

  const patientReasonText = (reason) => {
    const displayValue = patientDisplayValue(reason);
    return displayValue === "—" ? "Pending admin review" : displayValue;
  };

  const patientComparisonEntry = (comparison, field) => {
    const entry = comparison[field];
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return {};
    return entry;
  };

  const patientComparisonSimilarity = (entry) => {
    const similarity = entry.similarity;
    if (typeof similarity === "number") return similarity;
    if (typeof similarity === "string" && similarity.trim()) {
      const parsed = Number(similarity);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  };

  const patientComparisonMatch = (entry, field) => {
    const value = entry[field];
    return value === true || value === "true";
  };

  const patientMatchMetadata = (u) => patientObjectValue(u.patient_match_metadata);

  const patientSuggestedMatch = (meta) => patientObjectValue(meta.suggested_match);

  const patientSubmittedFields = (meta) => patientObjectValue(meta.submitted);

  const patientCandidateName = (suggested) => patientObjectValue(suggested.name_parts);

  const patientSubmittedField = (submitted, u, submittedField, rowField) => (
    firstPatientDisplayValue(submitted[submittedField], u[rowField])
  );

  const patientSuggestedField = (suggested, field) => (
    firstPatientDisplayValue(suggested[field])
  );

  const patientCandidateNameField = (candidateName, field) => (
    firstPatientDisplayValue(candidateName[field])
  );

  const patientMetadataFlag = (value) => (
    value === true || value === "true" || value === 1 || value === "1"
  );

  const patientMetadataText = (value) => (
    typeof value === "string" ? value.trim() : ""
  );

  const isVerifiedPatientStatus = (status) => (
    patientMetadataText(status) === "verified"
  );

  const isNoSimilarPatientMatch = (meta) => (
    ["no_similar_patient", "none"].includes(patientMetadataText(meta.match_type))
  );

  // A signup counts as "approved" ONLY once staff have verified it
  // (patient_match_status === 'verified'). We deliberately ignore the match-time
  // `auto_approved` metadata flag: in manual-approval mode every signup — even an
  // exact match — stays pending until a human approves it, so an exact match must
  // never render as approved on its own.
  const isApprovedPatient = (u) => isVerifiedPatientStatus(u.patient_match_status);

  // Schema names this patient is actually matched to, most authoritative first:
  // the admin-confirmed schema, then the single suggested chart, then the
  // multi-chart candidate list (multiple_exact_matches), then the exact-match
  // rows (one per visit, usually all in the same schema). `searched_schemas` and
  // `failed_schemas` are deliberately ignored — they record where we LOOKED, not
  // where the patient was found, and showing them would imply a match.
  const patientMatchedSchemas = (u) => {
    const meta = patientMatchMetadata(u);
    const listSchemas = (value) => (
      Array.isArray(value) ? value.map((item) => patientObjectValue(item).schema) : []
    );
    const seen = new Set();
    return [
      u.patient_verified_schema,
      patientSuggestedMatch(meta).schema,
      ...listSchemas(meta.candidate_matches),
      ...listSchemas(meta.matched_rows),
    ].reduce((acc, value) => {
      const schema = patientMetadataText(value);
      // Dedupe case-insensitively: the same schema can arrive as "STUDENT" from
      // the verified column and "student" from the match metadata.
      const key = schema.toLowerCase();
      if (!schema || seen.has(key)) return acc;
      seen.add(key);
      acc.push(schema);
      return acc;
    }, []);
  };

  // One badge per matched schema. The admin-confirmed schema renders solid so a
  // reviewer can tell at a glance which one was actually approved versus merely
  // suggested by the matcher.
  const renderPatientSchemaBadges = (u) => {
    const schemas = patientMatchedSchemas(u);
    if (schemas.length === 0) return null;
    const verifiedSchema = patientMetadataText(u.patient_verified_schema).toLowerCase();
    const approved = isApprovedPatient(u);
    // A schema belongs to at most one clinic group, so the group is derivable
    // from the confirmed schema — nothing group-related is stored on the user.
    const clinic = groupForSchema(schemaGroupRows, u.patient_verified_schema);
    return (
      <HStack spacing={1} justify="flex-end" flexWrap="wrap">
        {clinic && (
          <Badge colorScheme="teal" variant="solid" title="Clinic group">
            clinic: {clinic}
          </Badge>
        )}
        {schemas.map((schema) => {
          const isConfirmed = approved && schema.toLowerCase() === verifiedSchema;
          return (
            <Badge
              key={schema}
              colorScheme="purple"
              variant={isConfirmed ? "solid" : "subtle"}
            >
              schema: {schema}{isConfirmed ? " · confirmed" : ""}
            </Badge>
          );
        })}
      </HStack>
    );
  };

  const patientMatchBadgeColor = (matchType) => (
    matchType === "approved" ? "green"
      : matchType === "denied" ? "gray"
      : matchType === "no_similar" ? "red"
      : "yellow"
  );

  const patientComparisonBadgeColor = (entry) => {
    if (patientComparisonMatch(entry, "exact") || patientComparisonMatch(entry, "both_blank")) return "green";
    if (patientComparisonSimilarity(entry) >= 0.82) return "yellow";
    return "orange";
  };

  const patientComparisonBadgeLabel = (entry) => {
    if (patientComparisonMatch(entry, "exact") || patientComparisonMatch(entry, "both_blank")) return "exact";
    if (patientComparisonSimilarity(entry) >= 0.82) return "similar";
    return "differs";
  };

  const patientComparisonBadge = (comparison, field) => {
    const entry = patientComparisonEntry(comparison, field);
    return (
      <Badge colorScheme={patientComparisonBadgeColor(entry)}>
        {patientComparisonBadgeLabel(entry)}
      </Badge>
    );
  };

  // An admin has explicitly denied this signup. Checked BEFORE the match-quality
  // states below: denial is a decision, not a match outcome, so a denied patient
  // must never sit in "needs review" implying an admin still has to act.
  const isDeniedPatient = (u) => (
    patientMetadataText(u.patient_match_status) === "denied"
  );

  const getPatientMatchType = (u) => {
    const meta = patientMatchMetadata(u);
    if (isApprovedPatient(u)) return "approved";
    if (isDeniedPatient(u)) return "denied";
    if (isNoSimilarPatientMatch(meta)) return "no_similar";
    return "needs_review";
  };

  // True while a patient has signed up but not yet confirmed the emailed/texted
  // code ('pending_verification'). They are NO LONGER hidden — an admin can
  // review and approve them directly — but the card shows an "awaiting code"
  // badge so it's clear they haven't proven contact ownership yet. (Kept
  // approvable because email/SMS verification can be unavailable.)
  const isAwaitingContactVerification = (u) => (
    patientMetadataText(u.patient_match_status) === "pending_verification"
  );

  // True once the patient has confirmed the code ('pending_review') — contact
  // ownership proven, awaiting admin approval.
  const isPendingReviewPatient = (u) => (
    patientMetadataText(u.patient_match_status) === "pending_review"
  );

  const getPatientSignupRows = () => {
    const rows = signupUsers
      .filter(isPatientSignupRow)
      .filter(hasPatientSignupData);
    return rows.filter((u) => {
      if (patientSignupStatusFilter === "all") return true;
      const matchType = getPatientMatchType(u);
      // "No similar patient" is no longer its own filter — those rows live under
      // "Needs review" (still distinguished by the red "no similar" badge on the
      // card). Everyone unapproved needs an admin decision either way.
      if (patientSignupStatusFilter === "needs_review") {
        return matchType === "needs_review" || matchType === "no_similar";
      }
      return matchType === patientSignupStatusFilter;
    });
  };

  const getPatientSignupCounts = () => {
    return signupUsers
      .filter(isPatientSignupRow)
      .filter(hasPatientSignupData)
      .reduce((acc, u) => {
        const matchType = getPatientMatchType(u);
        // Fold "no similar" into the needs_review bucket — it's not a separate
        // filter anymore, just a badge. Everyone unapproved needs review.
        // "denied" keeps its own bucket: those need no further admin action.
        const bucket = matchType === "no_similar" ? "needs_review" : matchType;
        acc[bucket] = (acc[bucket] || 0) + 1;
        acc.all += 1;
        return acc;
      }, { approved: 0, needs_review: 0, denied: 0, all: 0 });
  };

  const formatPatientSubmittedName = (u) => {
    const meta = patientMatchMetadata(u);
    const submitted = patientSubmittedFields(meta);
    return firstPatientDisplayValue(
      submitted.full_name,
      u.patient_full_name,
      patientLastFirstName(u.patient_last_name, u.patient_first_name, u.patient_middle_name),
    );
  };

  const renderPatientComparison = (u) => {
    const meta = patientMatchMetadata(u);
    const submitted = patientSubmittedFields(meta);
    const suggested = patientSuggestedMatch(meta);
    const candidateName = patientCandidateName(suggested);
    const comparison = patientObjectValue(meta.field_comparison);
    const matchType = getPatientMatchType(u);
    return (
      <Box borderWidth={0} borderRadius={0} p={3} bg="gray.50">
        <HStack justify="space-between" align="start" mb={3}>
          <Box>
            <Text fontWeight="semibold">{formatPatientSubmittedName(u)}</Text>
            <Text fontSize="xs" color="gray.500">
              {patientReasonText(meta.reason)}
            </Text>
          </Box>
          <VStack align="end" spacing={1}>
            <Badge colorScheme={patientMatchBadgeColor(matchType)}>
              {matchType.replace("_", " ")}
            </Badge>
            {isAwaitingContactVerification(u) ? (
              <Badge colorScheme="orange" variant="subtle">awaiting code</Badge>
            ) : isPendingReviewPatient(u) ? (
              <Badge colorScheme="green" variant="subtle">contact verified</Badge>
            ) : null}
            {renderPatientSchemaBadges(u)}
            <HStack spacing={2} align="start">
              <Text fontSize="xs" color="gray.500" mt="1px">Signed up</Text>
              {u.created_at ? formatSignupTime(u.created_at, viewerTimezone) : <Text fontSize="xs">—</Text>}
            </HStack>
            {isApprovedPatient(u) && u.patient_verified_at && (
              <HStack spacing={2} align="start">
                <Text fontSize="xs" color="green.600" mt="1px">Approved</Text>
                {formatSignupTime(u.patient_verified_at, viewerTimezone)}
              </HStack>
            )}
          </VStack>
        </HStack>
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Field</Th>
              <Th>Patient entered</Th>
              <Th>Suggested notes-c patient</Th>
              <Th>Status</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr><Td>First</Td><Td>{patientSubmittedField(submitted, u, "first_name", "patient_first_name")}</Td><Td>{patientCandidateNameField(candidateName, "first")}</Td><Td>{patientComparisonBadge(comparison, "first_name")}</Td></Tr>
            <Tr><Td>Middle</Td><Td>{patientSubmittedField(submitted, u, "middle_name", "patient_middle_name")}</Td><Td>{patientCandidateNameField(candidateName, "middle")}</Td><Td>{patientComparisonBadge(comparison, "middle_name")}</Td></Tr>
            <Tr><Td>Last</Td><Td>{patientSubmittedField(submitted, u, "last_name", "patient_last_name")}</Td><Td>{patientCandidateNameField(candidateName, "last")}</Td><Td>{patientComparisonBadge(comparison, "last_name")}</Td></Tr>
            <Tr><Td>DOB</Td><Td>{patientSubmittedField(submitted, u, "dob", "patient_dob")}</Td><Td>{patientSuggestedField(suggested, "dob")}</Td><Td>{patientComparisonBadge(comparison, "dob")}</Td></Tr>
            <Tr><Td>Phone</Td><Td>{patientSubmittedField(submitted, u, "phone", "patient_phone")}<PhoneStatusBadge phone={patientSubmittedField(submitted, u, "phone", "patient_phone")} compact ml={1} /></Td><Td>{patientSuggestedField(suggested, "phone")}<PhoneStatusBadge phone={patientSuggestedField(suggested, "phone")} compact ml={1} /></Td><Td>{patientComparisonBadge(comparison, "phone")}</Td></Tr>
            <Tr><Td>Email</Td><Td>{patientSubmittedField(submitted, u, "email", "patient_email")}</Td><Td>{patientSuggestedField(suggested, "email")}</Td><Td>{patientComparisonBadge(comparison, "email")}</Td></Tr>
          </Tbody>
        </Table>
      </Box>
    );
  };

  // Pre-fill the confirm inputs from the suggested notes-c match ({schema, row_id}).
  // The patient's system-assigned identity. Unlike the EHR id it is always
  // present (the database assigns it on insert), so it's what to quote when
  // talking about a patient who has no chart yet. Read from the roster row the
  // signups payload carries, when present.
  const patientVitaId = (u) => (
    patientMetadataText(u.patient_vita_id)
    || patientMetadataText(patientMatchMetadata(u).vita_id)
  );

  // ehr_id is NOT NULL, so a patient without a real EHR carries their own VitaID
  // in that column. It's a real stored value — not blank — so the UI has to say
  // it's a placeholder rather than let an admin mistake it for an EHR id.
  const isVitaPlaceholder = (value) => /^VITA-/i.test(patientMetadataText(value));

  // Clinic groups, read straight from public.schema_settings (same as the Fax
  // Numbers modal reads fax_num). Drives both the Schema Groups editor's
  // refresh and the unmatched-patient dropdown below.
  const schemaGroups = useMemo(() => groupsFromRows(schemaGroupRows), [schemaGroupRows]);

  // Schemas an admin can approve a patient into — sourced ONLY from
  // public.schema_settings. See schemaOptionsFromRows for why an unconfigured
  // drafted value is still listed (marked) rather than dropped.
  const patientSchemaOptions = (u, draftSchema) =>
    schemaOptionsFromRows(schemaGroupRows, draftSchema);

  const suggestedApprovalDraft = (u) => {
    const suggested = patientSuggestedMatch(patientMatchMetadata(u));
    const rowIdRaw = suggested.row_id ?? suggested.id;
    const schemaRaw = suggested.schema;
    // Priority: the roster row's live value (which already holds either the real
    // EHR or the VITA- placeholder) → the matcher's suggested chart → the value
    // stamped on the user at approval. users.patient_ehr_id is written last, so
    // it lags and can't be the primary source.
    const ehrRaw = u.patient_roster_ehr_id ?? suggested.ehr_id ?? u.patient_ehr_id;
    return {
      schema: typeof schemaRaw === "string" ? schemaRaw.trim() : "",
      rowId: rowIdRaw === null || rowIdRaw === undefined ? "" : String(rowIdRaw).trim(),
      // Pre-filled from the suggested chart when there is one. Staff type it in
      // when the patient matched nothing — the EHR comes from the external
      // system, we never generate it. (Their identity is the VitaID, which the
      // database assigns automatically.)
      ehrId: ehrRaw === null || ehrRaw === undefined ? "" : String(ehrRaw).trim(),
    };
  };

  const getPatientApprovalDraft = (u) => patientApprovalDrafts[u.id] || suggestedApprovalDraft(u);

  const setPatientApprovalField = (u, field, value) =>
    setPatientApprovalFields(u, { [field]: value });

  // Several fields at once, so a change that invalidates another can clear it in
  // the SAME update. Picking a different clinic has to drop the ticked-schema
  // subset with it — two sequential setPatientApprovalField calls would leave a
  // render in between holding the old group's schemas against the new group.
  const setPatientApprovalFields = (u, patch) => {
    setPatientApprovalDrafts((prev) => {
      const base = prev[u.id] || suggestedApprovalDraft(u);
      return { ...prev, [u.id]: { ...base, ...patch } };
    });
  };

  // One place that turns an approval draft into both what the dropdowns show
  // and what Approve submits. The two used to derive it separately, which is how
  // a pre-filled grouped schema could render as a blank <select> while still
  // being submitted.
  const resolveApprovalChoice = (u) => {
    const draft = getPatientApprovalDraft(u);
    return {
      draft,
      ...resolveApprovalSelection(schemaGroupRows, draft.schema, draft.schemas),
    };
  };

  // Fetch the plain-English signup health report. live=1 also authenticates
  // against the email/SMS providers (no message is sent) to catch dead tokens.
  const runPatientSignupHealthCheck = async () => {
    setSignupHealthLoading(true);
    try {
      const res = await fetch(`${getBackendURL()}/api/admin/patient-signup-diagnostics/?live=1`);
      const data = await res.json();
      setSignupHealth(data);
      // Also print the plain-English report to the console for copy/paste.
      logDiagnosticsReport(data, 'admin health check');
    } catch (e) {
      setSignupHealth({
        ok: false,
        summary: `Could not run the health check: ${e.message}`,
        checks: [],
      });
    } finally {
      setSignupHealthLoading(false);
    }
  };

  const handleApprovePatient = async (u) => {
    // Same resolver the dropdowns render from, so what is submitted is exactly
    // what was on screen — including a matcher-prefilled schema shown as its
    // clinic with one member ticked.
    const { draft, group: resolvedGroup, picked: resolvedPicked, selectValue } =
      resolveApprovalChoice(u);
    const choice = (selectValue || "").trim();
    const verifiedRowId = (draft.rowId || "").trim();
    if (!choice) {
      toast({ title: "Schema is required", status: "warning", duration: 4000 });
      return;
    }
    // One <select> carries both kinds of option; split them apart here so the
    // request keeps `verified_schema` meaning exactly one schema, as it always
    // has. The backend expands `schema_group` into its member schemas.
    const parsed = parseGroupChoice(choice);
    const isGroup = parsed.kind === "group";
    const verifiedSchema = isGroup ? null : parsed.name;
    // Which of the group's schemas were ticked, already filtered against the
    // group's real membership by the resolver.
    const groupMembers = resolvedGroup ? resolvedGroup.schemas : [];
    const pickedSchemas = isGroup ? resolvedPicked : [];
    if (isGroup && groupMembers.length && !pickedSchemas.length) {
      toast({
        title: "Pick at least one schema",
        description: `Nothing is ticked in ${parsed.name}.`,
        status: "warning",
        duration: 4000,
      });
      return;
    }
    setPatientActionLoading(`${u.id}:approve`);
    try {
      const backendUrl = getBackendURL();
      const res = await fetch(`${backendUrl}/api/admin/approve-patient/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // Row id is optional: leave blank to approve for future appointments —
          // visits created later in this schema are matched by name + DOB.
          user_id: u.id,
          verified_schema: verifiedSchema,
          // Set only for a clinic-group approval; the backend rosters the
          // patient into every schema in the group and pins their portal to the
          // first of them.
          schema_group: isGroup ? parsed.name : null,
          // The ticked subset of that group. Omitted for a single schema, and
          // sent as the full list when everything is ticked — the backend
          // treats an absent/empty value as the whole group either way.
          schemas: isGroup ? pickedSchemas : null,
          verified_row_id: verifiedRowId || null,
          // Hand-entered EHR wins over the chart-derived one server-side; it's
          // the only source for a patient who matched no chart. A VITA- value is
          // the system's own placeholder, NOT an EHR — sending it would stamp a
          // fake EHR onto the user and let it match against charts.
          ehr_id: isVitaPlaceholder(draft.ehrId)
            ? null
            : (draft.ehrId || "").trim() || null,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.error) throw new Error(json.error || "Approval failed");
      // Report which rosters they actually landed on. A group approval that
      // silently skipped a schema (missing patients_with_ehrid) would otherwise
      // look identical to one that worked.
      const rostered = Array.isArray(json.rostered_schemas) ? json.rostered_schemas : [];
      const skipped = Array.isArray(json.skipped_schemas) ? json.skipped_schemas : [];
      toast({
        title: json.schema_group
          ? `Patient approved into ${json.schema_group}`
          : "Patient approved",
        description: [
          rostered.length ? `Added to: ${rostered.join(", ")}.` : "",
          skipped.length ? `No patient table in: ${skipped.join(", ")}.` : "",
        ]
          .filter(Boolean)
          .join(" ") || undefined,
        status: skipped.length ? "warning" : "success",
        duration: skipped.length ? 8000 : 4000,
        isClosable: true,
      });
      fetchSignupUsers(); // reuse the dashboard's existing refetch
    } catch (err) {
      toast({ title: "Patient approval failed", description: err.message, status: "error", duration: 5000 });
    } finally {
      setPatientActionLoading(null);
    }
  };

  const handleDenyPatient = async (u) => {
    setPatientActionLoading(`${u.id}:deny`);
    try {
      const backendUrl = getBackendURL();
      const res = await fetch(`${backendUrl}/api/admin/deny-patient/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: u.id }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.error) throw new Error(json.error || "Deny failed");
      toast({ title: "Patient signup denied", status: "info", duration: 3000, isClosable: true });
      fetchSignupUsers(); // reuse the dashboard's existing refetch
    } catch (err) {
      toast({ title: "Deny failed", description: err.message, status: "error", duration: 5000 });
    } finally {
      setPatientActionLoading(null);
    }
  };

  // Editable confirm-the-match row + Confirm & Approve / Deny for a non-verified
  // patient signup. Rendered under renderPatientComparison in the Patient Signups tab.
  const renderPatientApprovalControls = (u) => {
    const status = patientMetadataText(u.patient_match_status);
    if (!status || status === "verified") return null;
    const meta = patientMatchMetadata(u);
    const suggested = patientSuggestedMatch(meta);
    const draft = getPatientApprovalDraft(u);
    // The list is always structured: clinic groups, then the schemas that
    // aren't in one. A grouped schema is NOT listed on its own — you reach it by
    // picking its clinic and unticking the rest in the checkbox dropdown, which
    // is what keeps one control from listing the same schema twice under two
    // different meanings. Gating on schemaGroups (not schemaGroupRows) means a
    // database that predates the "Group" column, or simply has no groups
    // configured, still gets the plain flat list.
    const offerGroups = schemaGroups.length > 0;
    // Everything carrying no group label. A drafted-but-unconfigured schema
    // falls through here too (patientSchemaOptions keeps it for the
    // blank-Select reason explained there).
    const groupless = offerGroups
      ? patientSchemaOptions(u, draft.schema).filter(
          (opt) => !groupForSchema(schemaGroupRows, opt.value)
        )
      : [];
    const { group: selectedGroup, members: groupSchemas, picked: pickedSchemas,
            selectValue } = resolveApprovalChoice(u);
    const allPicked = pickedSchemas.length === groupSchemas.length;
    // A group with nothing ticked can't be approved — say so on the button
    // rather than letting the backend reject it after a round trip.
    const noSchemaPicked = !!selectedGroup && pickedSchemas.length === 0;
    const approving = patientActionLoading === `${u.id}:approve`;
    const denying = patientActionLoading === `${u.id}:deny`;
    const busy = approving || denying;
    const reason = patientMetadataText(meta.reason);
    return (
      <Box borderWidth={0} borderTopWidth={1} borderColor="gray.200" borderRadius={0} p={3} bg="white">
        <VStack align="stretch" spacing={3}>
          <HStack spacing={3} flexWrap="wrap">
            <HStack spacing={2}>
              <Text fontSize="xs" color="gray.500">Match status</Text>
              <Badge colorScheme={status === "denied" ? "red" : "orange"}>
                {status.replace(/_/g, " ")}
              </Badge>
            </HStack>
            {reason && (
              <Text fontSize="xs" color="gray.500">Reason: {reason}</Text>
            )}
            <Text fontSize="xs" color="gray.500">
              Suggested: {firstPatientDisplayValue(suggested.patient_name)} · DOB {firstPatientDisplayValue(suggested.dob)}
            </Text>
          </HStack>
          <HStack spacing={3} align="flex-end" flexWrap="wrap">
            <FormControl maxW="200px">
              <FormLabel fontSize="xs" mb={1}>Confirm schema</FormLabel>
              {/* Was a free-text box, which meant an admin had to already know
                  the exact schema name — and the Approve button stays disabled
                  until it's filled, so an unmatched patient looked unapprovable.
                  The matcher already reports every schema it searched, so offer
                  those directly. */}
              <Select
                size="sm"
                value={selectValue}
                placeholder={
                  offerGroups ? "choose a clinic or schema" : "choose a schema"
                }
                onChange={(e) =>
                  // Clear the ticked subset with the group it belonged to, so
                  // switching clinics never carries the old clinic's schemas
                  // across. Unset = every schema in the new group.
                  setPatientApprovalFields(u, {
                    schema: e.target.value,
                    schemas: null,
                  })
                }
              >
                {/* Clinic groups first, then the schemas that aren't in one.
                    Picking a group means the whole clinic; the checkbox
                    dropdown beside this narrows it to some of its schemas, or
                    to exactly one. A grouped schema deliberately does NOT also
                    appear as its own option — the same name in one list meaning
                    both "this doctor" and "this doctor's clinic" is how an
                    admin ends up rostering into four schemas by accident. */}
                {offerGroups ? (
                  <>
                    {schemaGroups.length > 0 && (
                      <optgroup label="Clinic groups">
                        {schemaGroups.map((g) => (
                          <option key={g.key} value={groupChoiceValue(g.name)}>
                            {g.name} ({g.schemas.length} schema
                            {g.schemas.length === 1 ? "" : "s"})
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {groupless.length > 0 && (
                      <optgroup label="Schemas not in a group">
                        {groupless.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </optgroup>
                    )}
                  </>
                ) : (
                  patientSchemaOptions(u, draft.schema).map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))
                )}
              </Select>
              <Text fontSize="xs" color="gray.400" mt={1}>
                {schemaGroupRows.length === 0
                  ? "Couldn't read schema_settings — no schemas to choose from. Reopen this dashboard to retry."
                  : selectedGroup
                    ? noSchemaPicked
                      ? "Pick at least one schema in this clinic."
                      : `Adds them to the roster of ${pickedSchemas.length}${
                          allPicked ? "" : ` of ${groupSchemas.length}`
                        }: ${pickedSchemas.join(", ")}.`
                    : "Required. Anyone can be approved into a schema — even with no chart match."}
              </Text>
            </FormControl>
            {/* Narrow a clinic to some of its doctors. A group used to be
                all-or-nothing, so approving a patient into two schemas of a
                five-schema clinic meant approving into all five and undoing
                three by hand. Only rendered for a group choice — a single
                schema has nothing to narrow. */}
            {selectedGroup && (
              <FormControl maxW="220px">
                <FormLabel fontSize="xs" mb={1}>Schemas in this clinic</FormLabel>
                <Menu closeOnSelect={false} isLazy>
                  <MenuButton
                    as={Button}
                    size="sm"
                    variant="outline"
                    w="100%"
                    fontWeight="normal"
                    textAlign="left"
                    colorScheme={noSchemaPicked ? "red" : undefined}
                  >
                    {noSchemaPicked
                      ? "None selected"
                      : allPicked
                        ? `All ${groupSchemas.length} schema${groupSchemas.length === 1 ? "" : "s"}`
                        : `${pickedSchemas.length} of ${groupSchemas.length} selected`}
                  </MenuButton>
                  {/* Portalled: this menu opens inside a scrolling signups
                      panel, which would otherwise clip it. */}
                  <Portal>
                    <MenuList maxH="260px" overflowY="auto" zIndex="popover" minW="220px">
                      <MenuOptionGroup
                        type="checkbox"
                        value={pickedSchemas}
                        onChange={(next) =>
                          // Store [] as [] rather than null: null means "not
                          // chosen yet" and re-ticks everything, which would
                          // silently undo unticking the last one. Filtered
                          // inline rather than through filterGroupSubset —
                          // that reads [] as "no narrowing" (wire-compat with
                          // the backend) and would hand back the whole clinic.
                          setPatientApprovalField(
                            u,
                            "schemas",
                            (next || []).filter((n) => groupSchemas.includes(n))
                          )
                        }
                      >
                        {groupSchemas.map((name) => (
                          <MenuItemOption key={name} value={name} fontSize="sm">
                            {name}
                          </MenuItemOption>
                        ))}
                      </MenuOptionGroup>
                      <MenuDivider />
                      <HStack spacing={2} px={3} pb={2} pt={1}>
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() => setPatientApprovalField(u, "schemas", null)}
                        >
                          Select all
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() => setPatientApprovalField(u, "schemas", [])}
                        >
                          Clear
                        </Button>
                      </HStack>
                    </MenuList>
                  </Portal>
                </Menu>
                <Text fontSize="xs" color="gray.400" mt={1}>
                  {allPicked
                    ? "Whole clinic. Untick any you don't want."
                    : `Skipping: ${groupSchemas
                        .filter((s) => !pickedSchemas.includes(s))
                        .join(", ")}.`}
                </Text>
              </FormControl>
            )}
            {/* The notes-c row id used to be an editable field here. It was an
                opaque uuid an admin had no way to reason about, and it existed
                only to look up the visit's EHR id — which the EHR field beside
                this now shows directly. The suggested row id is still submitted
                (see handleApprovePatient) so the backend keeps validating the
                chart and linking every visit under that EHR. */}
            <FormControl maxW="200px">
              <FormLabel fontSize="xs" mb={1}>EHR ID</FormLabel>
              <Input
                size="sm"
                value={draft.ehrId}
                placeholder="type it in if unmatched"
                onChange={(e) => setPatientApprovalField(u, "ehrId", e.target.value)}
              />
              <Text fontSize="xs" color="gray.400" mt={1}>
                {/* A VITA- value here is the system placeholder standing in
                    until a real EHR exists — say so, or an admin will read it as
                    a real id and leave it alone. */}
                {isVitaPlaceholder(draft.ehrId)
                  ? "Placeholder — replace with the real EHR ID when you have it."
                  : "Assigned by the EHR — we never generate it."}
              </Text>
            </FormControl>
            <HStack spacing={2}>
              <Button
                size="sm"
                colorScheme="green"
                isLoading={approving}
                isDisabled={busy || !draft.schema.trim() || noSchemaPicked}
                onClick={() => handleApprovePatient(u)}
              >
                {/* "Confirm" once we have something to link them to — either a
                    chart the matcher found, or an EHR the admin typed. Without
                    either, this is a future-visits approval matched on name + DOB. */}
                {draft.rowId.trim() || (draft.ehrId.trim() && !isVitaPlaceholder(draft.ehrId))
                  ? "Confirm & Approve"
                  : "Approve for future visits"}
              </Button>
              <Button
                size="sm"
                colorScheme="red"
                variant="outline"
                isLoading={denying}
                isDisabled={busy}
                onClick={() => handleDenyPatient(u)}
              >
                Deny
              </Button>
            </HStack>
          </HStack>
        </VStack>
      </Box>
    );
  };

  const handleApproveUser = async (userId) => {
    setApprovingUserId(userId);
    try {
      const backendUrl = getBackendURL();
      const res = await fetch(`${backendUrl}/api/admin/approve-user/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || "Approval failed");
      // Update local state immediately
      setSignupUsers((prev) =>
        prev.map((u) => u.id === userId ? { ...u, role: json.approved_role } : u)
      );
      toast({ title: `Approved as ${json.approved_role}`, status: "success", duration: 3000, isClosable: true });
      // Refresh so the new approval-email status row appears.
      fetchSignupUsers();
    } catch (err) {
      toast({ title: "Approval failed", description: err.message, status: "error", duration: 4000 });
    } finally {
      setApprovingUserId(null);
    }
  };

  // Union of every schema already in use across the loaded signups, so the
  // dropdown offers real schema names without needing a list-schemas endpoint.
  const knownSchemas = useMemo(() => {
    const seen = new Set();
    signupUsers.forEach((u) => {
      String(u.schema || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((s) => seen.add(s));
    });
    return Array.from(seen).sort();
  }, [signupUsers]);

  // User Access tab: it searches users outside the loaded signups period, so
  // offer every schema in schema_settings too, not only the ones seen here.
  const accessTabSchemas = useMemo(() => {
    const seen = new Set(knownSchemas);
    schemaGroupRows.forEach((r) => r?.schema_name && seen.add(r.schema_name));
    return Array.from(seen).sort();
  }, [knownSchemas, schemaGroupRows]);

  const openAccessModal = (u) => {
    const schemas = parseSchemaList(u.schema);
    setAccessDraft({ nativeInput: schemas[0] || "", base: schemas, extraInput: "" });
    setSchemaTargetUser(u);
  };

  // The draft keeps the pre-edit list (`base`); the saved list moves the
  // native input to the front (swapping with an extra, or pushing the old
  // native into the extras) — see setNativeSchema. A blank input leaves the
  // list alone and disables Save instead.
  const setAccessNative = (value) => setAccessDraft((d) => ({ ...d, nativeInput: value }));
  const addAccessExtra = (name) =>
    setAccessDraft((d) => ({ ...d, extraInput: "", base: addDraftSchema(d.base, d.nativeInput, name) }));
  const removeAccessExtra = (name) =>
    setAccessDraft((d) => ({ ...d, base: removeDraftSchema(d.base, name) }));

  // Derived view of the draft. Cheap, and the modal is always mounted.
  const accessDraftSchemas = setNativeSchema(accessDraft.base, accessDraft.nativeInput);
  const accessCurrentSchemas = parseSchemaList(schemaTargetUser?.schema);
  const accessSchemasChanged =
    !!schemaTargetUser && accessDraftSchemas.join(",") !== accessCurrentSchemas.join(",");
  const accessCanSave =
    !!accessDraft.nativeInput.trim() && accessDraftSchemas.length > 0 && accessSchemasChanged;
  // Options for the Status column's role dropdown. superadmin is grantable
  // only by a superadmin (the backend enforces it too).
  const roleOptions = assignableSignupRoles().filter(
    (r) => r !== "superadmin" || isSuperAdminRole(getSupabaseRole()),
  );

  const describeAccessError = (json, status) => {
    switch (json?.error) {
      case "superadmin_required":
        return "Only a superadmin can grant the superadmin role.";
      case "cannot_edit_own_role":
        return "You can't change your own role.";
      case "caller_not_admin":
        return "Your role can't edit user access.";
      case "invalid_schema_name":
        return `"${json.schema}" is not a valid schema name (letters, digits, underscores).`;
      case "invalid_role":
        return "That role can't be assigned from the signups dashboard.";
      case "superadmin_switch_required":
        return "Only a superadmin can change someone's superadmin switch.";
      case "cannot_edit_own_superadmin":
        return "You can't change your own superadmin switch.";
      default:
        return json?.error || `HTTP ${status}`;
    }
  };

  // The request itself: POST the change for `target`; the backend's answer, or a throw with a
  // readable message.
  const requestUserAccess = async (target, payload) => {
    const { data: { session } = {} } = await supabasePublic().auth.getSession();
    const token = session?.access_token;
    if (!token) throw new Error("No active session — sign in again");
    const res = await fetch(`${getBackendURL()}/api/admin/update-user-access/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ target_user_id: target.id, ...payload }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.error) throw new Error(describeAccessError(json, res.status));
    return json;
  };

  // Shared by the inline role dropdown and the Edit-access modal: POST the
  // change, apply the backend's canonical role/schemas to the row, and toast.
  // Throws (with a readable message) on failure.
  const postUserAccess = async (target, payload) => {
    const json = await requestUserAccess(target, payload);

    // Mirror the backend's canonical values so the row (dropdown, Accept
    // label, schema tooltip) updates without a refetch.
    setSignupUsers((prev) =>
      prev.map((u) =>
        u.id === target.id ? { ...u, role: json.role, schema: json.schemas.join(",") } : u,
      ),
    );
    const warnings = Array.isArray(json.warnings) ? json.warnings : [];
    if (warnings.length) console.warn("[signups-dashboard] update-user-access warnings:", warnings);
    toast({
      title: `Updated ${target.name || target.email}`,
      description:
        `Role: ${json.role} · Schemas: ${json.schemas.join(", ")}` +
        (warnings.length ? ` · ${warnings[0]}` : ""),
      status: warnings.length ? "warning" : "success",
      duration: warnings.length ? 9000 : 5000,
      isClosable: true,
    });
    return json;
  };

  // Status-column dropdown. A pending row keeps its prefix server-side
  // ("user,student" → "user,doctor"), so Accept stays and approves as the new
  // role. The select is controlled by the row, so a failed save snaps back.
  const handleChangeUserRole = async (u, newRole) => {
    if (!newRole || newRole === normalizeRole(u.role)) return;
    setRoleSavingUserId(u.id);
    try {
      await postUserAccess(u, { role: newRole });
    } catch (err) {
      toast({
        title: "Role change failed",
        description: err.message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setRoleSavingUserId(null);
    }
  };

  const handleSaveUserAccess = async () => {
    const target = schemaTargetUser;
    if (!target || !accessCanSave) return;
    setSavingAccess(true);
    try {
      await postUserAccess(target, { schemas: accessDraftSchemas });
      setSchemaTargetUser(null);
    } catch (err) {
      toast({
        title: "Update access failed",
        description: err.message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSavingAccess(false);
    }
  };

  // User Access tab save: same endpoint as the Edit access modal. Returns the
  // backend's canonical role/schemas; toasts and rethrows on failure.
  const handleSaveSearchedUserSchemas = async (target, schemas) => {
    try {
      return await postUserAccess(target, { schemas });
    } catch (err) {
      toast({
        title: "Update access failed",
        description: err.message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
      throw err;
    }
  };

  // User Access tab: a user's superadmin switch (users.superadmin). Toasts what happened; rethrows
  // on failure so the switch goes back.
  const handleSetSearchedUserSuperadmin = async (target, on) => {
    try {
      const json = await requestUserAccess(target, { superadmin: on });
      toast({
        title: `Superadmin ${on ? "on" : "off"} for ${target.name || target.email}`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      return json;
    } catch (err) {
      toast({
        title: "Superadmin switch not changed",
        description: err.message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
      throw err;
    }
  };

  const handleResendEmail = async (userId, kind) => {
    try {
      const backendUrl = getBackendURL();
      const res = await fetch(`${backendUrl}/api/admin/resend-email/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, kind }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      if (json.sent) {
        toast({
          title: `${kind === "welcome" ? "Welcome" : "Approval"} email re-sent`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Re-send failed",
          description: json.error || "unknown error",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
      // Refresh row so the status column reflects the new state.
      fetchSignupUsers();
    } catch (err) {
      toast({
        title: "Re-send request failed",
        description: err.message,
        status: "error",
        duration: 4000,
      });
    }
  };

  const toggleSignupSelected = (id) => {
    setSelectedSignupIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllFiltered = () => {
    const filtered = getFilteredSignups();
    const filteredIds = filtered.map((u) => u.id);
    const allSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedSignupIds.has(id));
    setSelectedSignupIds(allSelected ? new Set() : new Set(filteredIds));
  };

  const clearSignupSelection = () => setSelectedSignupIds(new Set());

  const dismissActiveJob = useCallback(() => setActiveJob(null), []);

  const fetchActiveBulkJob = async () => {
    try {
      const backendUrl = getBackendURL();
      const res = await fetch(`${backendUrl}/api/admin/bulk-jobs-active/`);
      const json = await res.json();
      if (Array.isArray(json.jobs) && json.jobs.length > 0) {
        setActiveJob(json.jobs[0]);
      }
    } catch (err) {
      console.warn("[signups-dashboard] fetchActiveBulkJob failed:", err);
    }
  };

  // Zoho's block-notification email lingers in the inbox for 24h after the
  // admin clicks "unblock". Two layers of suppression keep us from re-popping
  // a stale block email: (1) only auto-show within 15 minutes of receipt
  // (the "fresh" window — beyond that the admin can verify last-sent-at on
  // Recheck), (2) localStorage tracks per-email dismissal for the rare case
  // a fresh email is acknowledged but lingers in IMAP for the full window.
  const FRESH_WINDOW_MS = 15 * 60 * 1000;

  const isBlockEmailDismissed = (email) => {
    if (!email?.received_at) return false;
    try {
      const dismissed = localStorage.getItem("notesai.zohoBlockDismissedAt");
      return dismissed && email.received_at <= dismissed;
    } catch {
      return false;
    }
  };

  const isBlockEmailFresh = (email) => {
    if (!email?.received_at) return false;
    const t = Date.parse(email.received_at);
    if (Number.isNaN(t)) return false;
    const age = Date.now() - t;
    return age >= 0 && age <= FRESH_WINDOW_MS;
  };

  const formatBlockEmailTimestamp = (email) => {
    if (!email?.received_at) return null;
    const t = Date.parse(email.received_at);
    if (Number.isNaN(t)) return null;
    return new Date(t).toLocaleString();
  };

  const showBlockEmail = (email, { force = false } = {}) => {
    if (!email) return;
    if (!force) {
      if (isBlockEmailDismissed(email)) return;
      if (!isBlockEmailFresh(email)) return;
    }
    setBlockEmail(email);
  };

  const dismissBlockEmail = () => {
    if (blockEmail?.received_at) {
      try { localStorage.setItem("notesai.zohoBlockDismissedAt", blockEmail.received_at); }
      catch { /* localStorage disabled — fine, modal just won't auto-suppress */ }
    }
    setBlockEmail(null);
  };

  const fetchZohoBlockStatus = async ({ force = false, announce = false } = {}) => {
    try {
      if (announce) setZohoCheckLoading(true);
      const backendUrl = getBackendURL();
      const url = `${backendUrl}/api/admin/zoho-block-status/${force ? "?force=1" : ""}`;
      const res = await fetch(url);
      const json = await res.json();
      if (json.imap_error) {
        if (announce) toast({ title: "IMAP check failed", description: json.imap_error, status: "warning", duration: 5000 });
        return;
      }
      if (json.block_email) setLastBlockEmail(json.block_email);
      if (json.blocked) showBlockEmail(json.block_email, { force: announce });
      else if (announce) toast({ title: "No Zoho blocks detected", status: "success", duration: 3000 });
      if (announce && json.sync) {
        const { welcome_filled = 0, approval_filled = 0, roles_promoted = 0 } = json.sync;
        if (welcome_filled || approval_filled || roles_promoted) {
          toast({
            title: "Synced from Zoho Sent folder",
            description: `welcome: ${welcome_filled}, approval: ${approval_filled}, roles promoted: ${roles_promoted}`,
            status: "info",
            duration: 4000,
          });
          fetchSignupUsers();
        }
      }
      if (announce && json.block_email) {
        const stamp = formatBlockEmailTimestamp(json.block_email);
        if (stamp && !isBlockEmailFresh(json.block_email)) {
          toast({
            title: "Last Zoho block email",
            description: `Received ${stamp} — older than 15 min, not auto-popping.`,
            status: "info",
            duration: 5000,
          });
        }
      }
    } catch (err) {
      console.warn("[signups-dashboard] fetchZohoBlockStatus failed:", err);
      if (announce) toast({ title: "Zoho status check failed", description: err.message, status: "warning", duration: 4000 });
    } finally {
      if (announce) setZohoCheckLoading(false);
    }
  };

  const startBulkJob = async (kind) => {
    const ids = [...selectedSignupIds].filter((id) => {
      const u = signupUsers.find((x) => x.id === id);
      if (!u) return false;
      if (kind === "welcome") return !u.signup_emailed_at;
      return isPendingApprovable(u.role);
    });
    if (ids.length === 0) {
      toast({
        title: kind === "welcome"
          ? "All selected users already received a welcome email"
          : "No pending users selected",
        status: "info", duration: 3000,
      });
      return;
    }
    setBulkActionLoading(kind);
    try {
      const backendUrl = getBackendURL();
      const res = await fetch(`${backendUrl}/api/admin/bulk-send-emails/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind, user_ids: ids }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setActiveJob({
        job_id: json.job_id, kind, total: json.queued,
        sent: 0, failed: 0, remaining: json.queued,
        blocked: false, block_email: null, rate_wait_until: null,
        errors: [], completed_at: null,
      });
      clearSignupSelection();
    } catch (err) {
      toast({ title: "Bulk send failed to start", description: err.message, status: "error", duration: 4000 });
    } finally {
      setBulkActionLoading(null);
    }
  };

  const handleBulkSendWelcome = () => startBulkJob("welcome");
  const handleBulkApprove = () => startBulkJob("approval");

  const handleExportSignupsCSV = () => {
    const filtered = getFilteredSignups();
    const rows = selectedSignupIds.size > 0
      ? filtered.filter((u) => selectedSignupIds.has(u.id))
      : filtered;
    if (rows.length === 0) {
      toast({ title: "Nothing to export", status: "info", duration: 2500 });
      return;
    }
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = [
      "Name", "Email", "Signed Up (UTC)", `Signed Up (${viewerTimezone})`,
      "Schema", "Role", "Status", "Referred By", "Use Case",
      "Welcome Email Sent At", "Welcome Email Error",
      "Approval Email Sent At", "Approval Email Error",
    ];
    const lines = [headers.join(",")];
    rows.forEach((u) => {
      const { reason, referralSource, referralDetail } = parseSignupReason(u.signup_reason);
      const referralDisplay = referralDetail ? `${referralDetail} (${referralSource})` : (referralSource || "");
      const role = u.role || "";
      const status = isPending(role) ? "Pending" : "Approved";
      const createdUtc = u.created_at ? new Date(u.created_at).toISOString() : "";
      let createdLocal = "";
      if (u.created_at) {
        try {
          createdLocal = new Date(u.created_at).toLocaleString("en-US", {
            timeZone: viewerTimezone,
            dateStyle: "short", timeStyle: "short",
          });
        } catch { createdLocal = new Date(u.created_at).toLocaleString(); }
      }
      lines.push([
        u.name || "", u.email || "", createdUtc, createdLocal,
        u.schema || "", role, status, referralDisplay, reason || "",
        u.signup_emailed_at || "", u.signup_email_error || "",
        u.approval_emailed_at || "", u.approval_email_error || "",
      ].map(escape).join(","));
    });
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    a.download = `signups-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({
      title: `Exported ${rows.length} user${rows.length === 1 ? "" : "s"}`,
      status: "success", duration: 2500,
    });
  };

  const getReferralStats = () => {
    const validNames = new Set(
      signupUsers.map((u) => (u.name || "").trim().toLowerCase()).filter(Boolean)
    );
    const matched = {};
    const unmatched = {};

    signupUsers.forEach((u) => {
      const { referralSource, referralDetail } = parseSignupReason(u.signup_reason);
      const isReferral = (referralSource === "a friend" || referralSource === "other") && referralDetail.trim();
      if (!isReferral) return;
      const name = referralDetail.trim();
      const bucket = validNames.has(name.toLowerCase()) ? matched : unmatched;
      if (!bucket[name]) bucket[name] = { count: 0, users: [] };
      bucket[name].count++;
      bucket[name].users.push(u.name || u.email || "Unknown");
    });

    const toRows = (obj) =>
      Object.entries(obj)
        .sort((a, b) => b[1].count - a[1].count)
        .map(([referrer, data]) => ({ referrer, count: data.count, users: data.users }));

    return { matched: toRows(matched), unmatched: toRows(unmatched) };
  };

  const getRoleBadge = (role) => {
    if (!role) return <Badge colorScheme="gray">unknown</Badge>;
    if (normalizeRole(role) === "admin") return <Badge colorScheme="red">admin</Badge>;
    if (normalizeRole(role) === "doctor") return <Badge colorScheme="blue">doctor</Badge>;
    if (normalizeRole(role) === "student") return <Badge colorScheme="purple">student</Badge>;
    if (normalizeRole(role) === "socialworker") return <Badge colorScheme="orange">social worker</Badge>;
    if (role.includes(",")) return <Badge colorScheme="yellow">pending ({role.split(",")[1]?.trim()})</Badge>;
    if (normalizeRole(role) === "user") return <Badge colorScheme="yellow">pending</Badge>;
    return <Badge colorScheme="gray">{role}</Badge>;
  };

  const renderEmailStatusRow = (label, sentAt, error, kind, userId, isApplicable) => {
    if (!isApplicable) {
      return (
        <Text fontSize="xs" color="gray.400">
          {label}: —
        </Text>
      );
    }
    if (error) {
      return (
        <HStack spacing={1}>
          <Tooltip label={error} hasArrow placement="top">
            <Text fontSize="xs" color="red.500" cursor="help">
              {label}: failed ⚠
            </Text>
          </Tooltip>
          <Button
            size="xs"
            variant="link"
            colorScheme="blue"
            onClick={() => handleResendEmail(userId, kind)}
          >
            Retry
          </Button>
        </HStack>
      );
    }
    if (sentAt) {
      return (
        <HStack spacing={1}>
          <Tooltip label={new Date(sentAt).toLocaleString()} hasArrow placement="top">
            <Text fontSize="xs" color="green.600" cursor="help">
              {label}: sent ✓
            </Text>
          </Tooltip>
          <Button
            size="xs"
            variant="link"
            colorScheme="blue"
            onClick={() => handleResendEmail(userId, kind)}
          >
            Resend
          </Button>
        </HStack>
      );
    }
    return (
      <HStack spacing={1}>
        <Text fontSize="xs" color="gray.500">
          {label}: pending ⏳
        </Text>
        <Button
          size="xs"
          variant="link"
          colorScheme="blue"
          onClick={() => handleResendEmail(userId, kind)}
        >
          Send now
        </Button>
      </HStack>
    );
  };

  const getEmailStatusCell = (user) => {
    const userId = user.id;
    const approvalApplicable = !isPending(user.role);
    return (
      <VStack align="start" spacing={1}>
        {renderEmailStatusRow(
          "Welcome",
          user.signup_emailed_at,
          user.signup_email_error,
          "welcome",
          userId,
          true,
        )}
        {renderEmailStatusRow(
          "Approval",
          user.approval_emailed_at,
          user.approval_email_error,
          "approval",
          userId,
          approvalApplicable,
        )}
      </VStack>
    );
  };

  const handlePasswordSubmit = () => {
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      localStorage.setItem("isAdminAuthenticated", "true");
      onPasswordClose();
    } else {
      toast({
        title: "Error",
        description: "Incorrect password",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Schema creation only works from `public`.
  //
  // The RPC it calls lives in the public schema, and supabase.rpc() resolves
  // against the CLIENT's active schema — so from a tenant this failed with an
  // opaque 42883 "function does not exist" that read as "the setup script was
  // never installed". Refuse up front with the actual fix instead.
  const activeSchemaName = () => (supabaseSchema || "public").trim() || "public";

  const openCreateSchema = () => {
    const active = activeSchemaName();
    if (active !== "public") {
      toast({
        title: "Switch to public to create schema",
        description: `You're in "${active}". Schemas can only be created from the public schema — switch to it and try again.`,
        status: "warning",
        duration: 7000,
        isClosable: true,
      });
      return;
    }
    onCreateSchemaOpen();
  };

  const handleCreateSchema = async (schemaName, copyData = false, selectedTables = null) => {
    // Belt-and-braces: the button guards this, but the modal can also be opened
    // by a restored state, and a schema half-created from the wrong place is
    // far more painful than a refused click.
    const active = activeSchemaName();
    if (active !== "public") {
      toast({
        title: "Switch to public to create schema",
        description: `You're in "${active}". Schemas can only be created from the public schema.`,
        status: "warning",
        duration: 7000,
        isClosable: true,
      });
      return;
    }
    const supabase = await getSupabase();

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        toast({ title: "Not authenticated", status: "error", duration: 3000 });
        return;
      }

      // Create complete schema with credit tracking in one call
      toast({
        title: "Creating Complete Schema",
        description: `Creating schema "${schemaName}" with all tables and credit tracking${
          copyData ? " (copying data from current schema)" : " (empty tables)"
        }...`,
        status: "info",
        duration: 3000,
        isClosable: true,
      });

      const { data: schemaResult, error: schemaError } = await supabase.rpc(
        "create_complete_schema_with_credits",
        {
          dest_schema: schemaName,
          copy_data: copyData,
          tables_to_copy: selectedTables,
        },
      );

      if (schemaError) {
        // Check if the function doesn't exist yet
        if (schemaError.code === "42883") {
          toast({
            title: "Setup Required",
            description:
              "The schema creation function needs to be installed first. Please run the CREATE_COMPLETE_SCHEMA.sql script in Supabase SQL Editor.",
            status: "warning",
            duration: 8000,
            isClosable: true,
          });

          console.log(`
            =============================================
            SETUP REQUIRED
            =============================================

            The schema creation function is not installed yet.

            TO INSTALL:
            1. Open the file: sql/functions/CREATE_COMPLETE_SCHEMA.sql
            2. Copy the entire script
            3. Run it in Supabase SQL Editor
            4. Then try creating the schema again

            This only needs to be done ONCE per Supabase project.
            =============================================
          `);
          return;
        }

        // Check if schema already exists
        if (
          schemaError.message &&
          schemaError.message.includes("already exists")
        ) {
          toast({
            title: "Schema Already Exists",
            description: `Schema "${schemaName}" already exists. Please choose a different name.`,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          return;
        }

        throw schemaError;
      }

      // Parse the response (it may be JSON or a string)
      let resultData;
      try {
        resultData =
          typeof schemaResult === "string"
            ? JSON.parse(schemaResult)
            : schemaResult;
      } catch (e) {
        // If parsing fails, treat as plain string
        resultData = { message: schemaResult, missing_tables: [] };
      }

      // Register the new schema in public.schema_settings.
      //
      // That table is the app's roster of real schemas: the Providers dropdown,
      // the Schema groups editor, the patient-signup schema picker and the fax
      // destination-number lookup all read it. A schema missing from it exists
      // in Postgres but is invisible to the product, which looked like the
      // creation had half-failed.
      //
      // Non-fatal and idempotent — the schema itself is already created, and a
      // settings hiccup must not present that as a failure.
      try {
        const { error: settingsError } = await supabasePublic()
          .from("schema_settings")
          .upsert({ schema_name: schemaName }, { onConflict: "schema_name" });
        if (settingsError) throw settingsError;
      } catch (settingsErr) {
        console.warn("[create-schema] schema_settings row failed:", settingsErr?.message);
        toast({
          title: "Schema created, but not registered",
          description:
            `Could not add "${schemaName}" to schema_settings — it won't appear in ` +
            `Providers or Schema groups until a row exists. Add it manually.`,
          status: "warning",
          duration: 10000,
          isClosable: true,
        });
      }

      // Expose the schema in PostgREST. The Supabase access token this needs is
      // a secret, so the backend does it (POST /api/admin/expose-schema/ →
      // backend/api/schema_exposure.py) and only answers once PostgREST is
      // actually serving the schema. Before this the toast told the admin to do
      // it by hand in the dashboard, and a schema that was never exposed
      // answered every request with a 406/403 that read as broken auth.
      toast({
        title: "Exposing schema in the API",
        description: "Waiting for PostgREST to pick it up — this can take a minute.",
        status: "info",
        duration: 6000,
        isClosable: true,
      });
      let exposure;
      try {
        exposure = await exposeSchema(schemaName);
      } catch (exposeErr) {
        exposure = { ok: false, error: exposeErr.message };
      }

      if (exposure.ok) {
        toast({
          title: "Schema Created and Exposed",
          description:
            `Schema "${schemaName}" is created, exposed in the API and ready to use` +
            (exposure.waited_s
              ? ` (PostgREST picked it up after ${exposure.waited_s}s).`
              : "."),
          status: "success",
          duration: 10000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Schema created, but not exposed yet",
          description:
            `Schema "${schemaName}" was created, but exposing it in the API failed (${exposure.error}). ` +
            (exposure.manual_fix ||
              "Expose it in Supabase Dashboard → Project Settings → API → Exposed schemas, or every request to it will fail with an auth error."),
          status: "warning",
          duration: 15000,
          isClosable: true,
        });
      }

      // Show toast for missing tables if any
      if (resultData.missing_tables && resultData.missing_tables.length > 0) {
        toast({
          title: "Some Tables Not Found",
          description: `The following tables were not found in the public schema and were skipped: ${resultData.missing_tables.join(", ")}`,
          status: "warning",
          duration: 8000,
          isClosable: true,
        });
      }

      // Show completion message
      console.log(`
        =============================================
        COMPLETE SCHEMA CREATION SUCCESSFUL!
        =============================================

        Schema "${schemaName}" has been created with:
        ✅ Main tables: notes-c, no_show, no_show_logs, duplicates, logs
        ✅ Form tables: Forms, StoringFormLink, StoringFormSubmission
        ✅ Document tables: documents, document_backups, ai_feedback
        ✅ Other tables: SMS, corti_logs, users
        ✅ User credit management (credit columns)
        ✅ Row Level Security (RLS policies)
        ✅ Corti access control (corti_access column)
        ✅ Performance indexes
        ✅ Proper permissions and grants
        ${
          copyData
            ? "✅ Data copied from current schema"
            : "✅ Empty tables (no data copied)"
        }

        ⚠️  CRITICAL NEXT STEP - EXPOSE THE SCHEMA:

        1. Go to Supabase Dashboard → Project Settings → API → Exposed Schemas
        2. Add "${schemaName}" to the list of exposed schemas
        3. Save changes
        4. Without this step, you will get: "The schema must be one of the following" error

        ${copyData ? "VERIFY DATA:" : "ADD USERS:"}
           ${
             copyData
               ? `- Check that data was copied correctly to ${schemaName}`
               : "- Add users to the new schema's users table"
           }
           - Users will automatically get default credit limits (100.0)
           - Users get default corti_access = false

        3. TEST:
           - The schema is ready for immediate use
           - Credit tracking will work automatically
           - No additional scripts needed!

        =============================================
      `);
    } catch (error) {
      console.error("Create schema error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create schema.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSelectedRowChange = (row) => {
    setSelectedPatient(row);
    setEmail(row?.email || "");
    setPhone(row?.tel_num || "");
  };

  const handleDuplicatePatient = () => {
    if (!selectedPatient) {
      toast({
        title: "No Patient Selected",
        description: "Please select a patient to duplicate.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    setIsDuplicateModalOpen(true);
  };

  const fetchUserSettings = async () => {
    const supabase = await getSupabase();
    try {
      console.log("Fetching schema settings for:", supabaseSchema);

      // Query schema_settings table instead of users table
      const { data, error } = await supabasePublic()
        .from("schema_settings")
        .select("settings")
        .eq("schema_name", supabaseSchema)
        .single();

      if (error) {
        // If no record exists, create one with default settings for this schema
        // NOTE: Form URLs (NP_FORM_URL, NS_FORM_URL, CL1_FORM_URL, CL2_FORM_URL)
        // are REQUIRED and must be configured in schema_settings table
        if (error.code === "PGRST116") {
          const defaultSettings = {
            // NP_FORM_URL, NS_FORM_URL, CL1_FORM_URL, CL2_FORM_URL must be configured
            // No defaults - these MUST come from schema_settings table
            // No ZOHO_EMAIL / ZOHO_PASSWORD either: the backend falls back to
            // its .env sender, and a mailbox password never belongs in browser code.
            DOCUMENT_ROOT: "MedDocs/CustomSchema",
            TWILIO_PHONE_NUMBER: "+13236132761",
          };
          const { error: insertError } = await supabase
            .from("schema_settings")
            .insert({ schema_name: supabaseSchema, settings: defaultSettings });

          if (!insertError) {
            setUserSettings(defaultSettings);
            return;
          }
        }
        toast({
          title: "Failed to fetch settings",
          status: "error",
          duration: 3000,
        });
        console.error("Fetch settings error:", error);
        return;
      }

      console.log("Schema settings loaded:", data?.settings);
      setUserSettings(data?.settings || {});
    } catch (error) {
      console.error("Unexpected fetch error:", error);
      toast({
        title: "Error fetching settings",
        status: "error",
        duration: 3000,
      });
    }
  };

  const saveUserSettings = async () => {
    const supabase = await getSupabase();
    try {
      // Save to schema_settings table instead of users table
      const { error } = await supabasePublic().from("schema_settings").upsert(
        {
          schema_name: supabaseSchema,
          settings: userSettings,
        },
        { onConflict: "schema_name" },
      );

      if (error) {
        toast({
          title: "Failed to update settings",
          status: "error",
          duration: 3000,
        });
        console.error("Save settings error:", error);
        return;
      }

      toast({ title: "Settings updated", status: "success", duration: 3000 });
      onSettingsClose();
    } catch (error) {
      console.error("Unexpected save error:", error);
      toast({
        title: "Error saving settings",
        status: "error",
        duration: 3000,
      });
    }
  };

  // Open modal and fetch settings before showing it
  const handleOpenSettings = async () => {
    await fetchUserSettings();
    onSettingsOpen();
  };

  // Fetch messages_enabled setting for current schema
  const fetchMessagesEnabledSetting = async () => {
    // Ensures loadSupabaseSchema() has resolved so `supabaseSchema` reflects
    // the real tenant schema instead of its "public" default (see
    // fetchUserSettings above for the same requirement).
    await getSupabase();
    try {
      const supabase = await supabasePublic();

      const { data, error } = await supabase
        .from("schema_settings")
        .select("messages_enabled")
        .eq("schema_name", supabaseSchema)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          const { error: insertError } = await supabase
            .from("schema_settings")
            .insert({
              schema_name: supabaseSchema,
              messages_enabled: true,
            });

          if (!insertError) {
            setMessagesEnabled(true);
          }
          return;
        }
        console.error("Error fetching messages enabled setting:", error);
        return;
      }

      setMessagesEnabled(data?.messages_enabled ?? true);
    } catch (error) {
      console.error("Error fetching messages enabled setting:", error);
    }
  };

  // Toggle the messages_enabled master switch for current schema
  const toggleMessagesEnabledSetting = async () => {
    setLoadingMessagesEnabledSetting(true);
    try {
      await getSupabase();
      const supabase = await supabasePublic();
      const newValue = !messagesEnabled;

      const { error } = await supabase.from("schema_settings").upsert(
        {
          schema_name: supabaseSchema,
          messages_enabled: newValue,
        },
        { onConflict: "schema_name" },
      );

      if (error) {
        console.error("Error updating messages enabled setting:", error);
        toast({
          title: "Error",
          description: "Failed to update messages setting",
          status: "error",
          duration: 3000,
        });
        return;
      }

      setMessagesEnabled(newValue);
      toast({
        title: newValue ? "Messages Enabled" : "Messages Disabled",
        description: newValue
          ? `Outbound messages for ${supabaseSchema || "public"} are now allowed.`
          : `All outbound messages for ${supabaseSchema || "public"} are now blocked.`,
        status: newValue ? "success" : "warning",
        duration: 4000,
      });
    } catch (error) {
      console.error("Error updating messages enabled setting:", error);
      toast({
        title: "Error",
        description: "Failed to update messages setting",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoadingMessagesEnabledSetting(false);
    }
  };

  const openFaxNumbers = async () => {
    onFaxNumbersOpen();
    setFaxNumbersLoading(true);
    try {
      const { data, error } = await supabasePublic()
        .from("schema_settings")
        .select("schema_name, fax_num")
        .order("schema_name", { ascending: true });
      if (error) throw error;
      // Skip invalid multi-schema rows like 'public,Clinic5'
      const rows = (data || []).filter(
        (r) => r.schema_name && !r.schema_name.includes(",") && !r.schema_name.includes(" ")
      );

      // Fetch per-schema SRFax credential status (access_id + has_password; never the password)
      let credsBySchema = {};
      try {
        const res = await fetch(`${getBackendURL()}/api/admin/schema-srfax-credentials/`);
        if (res.ok) {
          const json = await res.json();
          credsBySchema = json.credentials || {};
        }
      } catch (e) {
        console.warn("Could not load SRFax credential status:", e);
      }

      setFaxSchemas(
        rows.map((r) => ({
          schema_name: r.schema_name,
          fax_num: r.fax_num || "",
          access_id: credsBySchema[r.schema_name]?.access_id || "",
          has_password: !!credsBySchema[r.schema_name]?.has_password,
          new_password: "",
        }))
      );
    } catch (e) {
      toast({
        title: "Failed to load schemas",
        description: e.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setFaxSchemas([]);
    } finally {
      setFaxNumbersLoading(false);
    }
  };

  const updateFaxNumberField = (schemaName, value) => {
    setFaxSchemas((prev) =>
      prev.map((r) => (r.schema_name === schemaName ? { ...r, fax_num: value } : r))
    );
  };

  const handleRunMigration = async (migrationConfig) => {
    const backendUrl = getBackendURL();
    try {
        const res = await fetch(`${backendUrl}/api/migration/run/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(migrationConfig),
        });
        const data = await res.json();
        return data;
    } catch (error) {
        console.error("Migration request failed:", error);
        throw error;
    }
  };



  const updateCredField = (schemaName, field, value) => {
    setFaxSchemas((prev) =>
      prev.map((r) => (r.schema_name === schemaName ? { ...r, [field]: value } : r))
    );
  };


  const saveAllFaxSettings = async () => {
    setSavingAllFax(true);
    const failures = [];
    for (const row of faxSchemas) {
      // Save fax number (idempotent; blank => system default)
      try {
        const trimmed = (row.fax_num || "").trim();
        const { error } = await supabasePublic()
          .from("schema_settings")
          .update({ fax_num: trimmed === "" ? null : trimmed })
          .eq("schema_name", row.schema_name);
        if (error) throw error;
      } catch (e) {
        failures.push(`${row.schema_name} (fax #): ${e.message}`);
      }
      // Save SRFax credentials only when there's something to write
      const aid = (row.access_id || "").trim();
      const pwd = (row.new_password || "").trim();
      if (aid || pwd) {
        try {
          const res = await fetch(`${getBackendURL()}/api/admin/schema-srfax-credentials/set/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              schema_name: row.schema_name,
              access_id: aid,
              access_pwd: pwd, // blank => password unchanged
            }),
          });
          const json = await res.json();
          if (!res.ok || !json.success) throw new Error(json.error || "Save failed");
        } catch (e) {
          failures.push(`${row.schema_name} (creds): ${e.message}`);
        }
      }
    }
    if (failures.length === 0) {
      // Clear write-only password fields; mark rows that just got a password as configured
      setFaxSchemas((prev) =>
        prev.map((r) => ({
          ...r,
          has_password: r.has_password || !!(r.new_password || "").trim(),
          new_password: "",
        }))
      );
      toast({
        title: "Saved",
        description: "Fax numbers and SRFax credentials saved.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } else {
      toast({
        title: "Some saves failed",
        description: failures.join("; "),
        status: "error",
        duration: 8000,
        isClosable: true,
      });
    }
    setSavingAllFax(false);
  };

  const handleFormTypeChange = (type) => (e) => {
    const isChecked = e.target.checked;
    setFormType(isChecked ? [type] : []);
  };

  const handleDuplicateSave = async (newPatientData) => {
    const supabase = await getSupabase();
    try {
      // Log the data being sent to Supabase for debugging
      console.log("Attempting to insert patient data:", newPatientData);

      // Remove the id and any other fields that should not be duplicated
      const { id, created_at, updated_at, ...patientData } = newPatientData;

      // Validate required fields (adjust based on your table schema)
      if (!patientData.name || !patientData.email) {
        throw new Error("Name and email are required fields.");
      }

      // Ensure email uniqueness by appending a timestamp if needed
      const timestamp = Date.now();
      patientData.email = patientData.email.includes("@")
        ? `${patientData.email.split("@")[0]}+${timestamp}@${
            patientData.email.split("@")[1]
          }`
        : patientData.email;

      // Insert the new patient record
      const { data, error } = await supabase
        .from("patients")
        .insert([patientData])
        .select()
        .single();

      if (error) {
        console.error("Supabase error details:", error);
        throw error;
      }
      // Phase D (utils/rdsCutover.js `write_patients`): see Schedules.js.
      mirrorWriteToRds("write_patients", supabaseSchema || "public", data);

      // Update local state to reflect the new patient in SchedulesTable
      setPatients([...patients, data]);
      toast({
        title: "Success",
        description: "Patient duplicated successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setIsDuplicateModalOpen(false);
    } catch (error) {
      console.error("Error duplicating patient:", error);
      toast({
        title: "Error",
        description: `Failed to duplicate patient: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const sendForms = async () => {
    if (!selectedPatient) {
      toast({
        title: "No Patient Selected",
        description: "Please select a patient to send forms to.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Skip form type validation for FFP/FFS patients as they use existing follow-up forms
    const patientType =
      findPatientField(selectedPatient, "type")?.toUpperCase() || "";
    if (!patientType.includes("FF") && formType.length === 0) {
      toast({
        title: "No Form Type Selected",
        description: "Please select at least one form type (NP or NS).",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const badNumberGuard = await confirmBadNumbers(
      contactMethods.phone && phone.trim() ? [{ phone }] : [],
    );
    if (!badNumberGuard) return;

    setIsSendingForms(true);
    try {
      // Create the base request data
      await fetchUserSettings();

      // Check if patient is FFP/FFS and use existing follow-up forms
      let formUrl = "";

      if (patientType.includes("FF")) {
        // For FFP/FFS patients, retrieve existing follow-up forms
        console.log(
          "[FFP/FFS] Retrieving existing follow-up forms for patient",
        );
        const submissionLinks = await extractAllSubmissionLinks(
          selectedPatient.id,
        );

        if (submissionLinks && submissionLinks.length > 0) {
          // Use the first available follow-up form
          formUrl = submissionLinks[0];
          console.log("[FFP/FFS] Using existing follow-up form:", formUrl);
        } else if (selectedPatient.submission_url) {
          // Use the submission_url as fallback
          formUrl = selectedPatient.submission_url;
          console.log("[FFP/FFS] Using submission_url as fallback:", formUrl);
        } else {
          toast({
            title: "No Follow-up Form Available",
            description: `Unable to send form for this ${patientType} patient. Please generate the ${patientType} form first using the "Generate Form" button.`,
            status: "error",
            duration: 6000,
            isClosable: true,
          });
          return;
        }
      } else {
        // For non-FF patients, get form URL from form type (from schema_settings)
        const formUrls = getFormUrls(userSettings);
        if (formType.includes("NP")) {
          formUrl = formUrls.NP_FORM_URL;
        } else if (formType.includes("NS")) {
          formUrl = formUrls.NS_FORM_URL;
        } else if (formType.includes("FF")) {
          // For FFP/FFS patients, formUrl will be set later from submission links
          formUrl = ""; // Will be set from submission links
        }
      }

      const requestData = {
        df1: {
          name: selectedPatient?.[colid2] || "",
          date_: selectedPatient?.date_ || "",
          appointment_time: selectedPatient?.time_ || "",
          form_type: patientType.includes("FF") ? patientType : formType[0],
          form_url: formUrl,
          phone:
            contactMethods.phone && phone.trim()
              ? phone.replace(/\D/g, "")
              : "", // Clean phone number
          Email: contactMethods.email && email.trim() ? email : "", // Email from input
          zoho_email: userSettings.ZOHO_EMAIL || "", // Include ZOHO_EMAIL from userSettings
          zoho_password: userSettings.ZOHO_PASSWORD || "",
          twilio_phone: userSettings.TWILIO_PHONE_NUMBER || "", // Include TWILIO_PHONE_NUMBER from userSettings
          contact_methods: contactMethods, // Include contact methods
          confirm_bad_number: badNumberGuard.confirmed(phone),
        },
        fpath: "manual",
        schema: supabaseSchema, // For backend to store in correct schema
      };
      const sendFormsUrl = getSendFormsUrl();
      console.log("[DEBUG] Sending forms to URL:", sendFormsUrl);
      console.log(
        "[DEBUG] Request data:",
        JSON.stringify(requestData, null, 2),
      );
      console.log(
        "[DEBUG] Selected patient:",
        JSON.stringify(selectedPatient, null, 2),
      );

      const response = await fetch(sendFormsUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      console.log("[DEBUG] Response status:", response.status);
      console.log("[DEBUG] Response status text:", response.statusText);
      console.log("[DEBUG] Response ok:", response.ok);
      console.log("[DEBUG] Response headers:", response.headers);

      let responseData;
      try {
        responseData = await response.json();
        console.log("[DEBUG] Response data:", responseData);
      } catch (jsonError) {
        console.log("[DEBUG] Failed to parse response JSON:", jsonError);
        console.log("[DEBUG] Raw response text:", await response.text());
        throw new Error("Invalid response format from server");
      }

      if (response.ok) {
        toast({
          title: "Form Sent",
          description: "The form(s) were sent successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        onSendFormClose();
      } else {
        throw new Error(responseData.message || "Failed to send forms");
      }
    } catch (error) {
      console.error("Error sending forms:", error);
      toast({
        title: "Send Failed",
        description:
          error.message || "An error occurred while sending the form.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSendingForms(false);
    }
  };

  const columns = [
    { header: "Name", accessor: "name" },
    {
      header: "Forms URL",
      accessor: "forms_url",
      render: (row) => (
        <Button onClick={() => navigate(`/forms/${row.id}`)}>as</Button>
      ),
    },
    {
      header: "Message",
      accessor: "message",
      render: (row) => (
        <Button onClick={() => navigate(`/messages/${row.id}`)}>Message</Button>
      ),
    },
    {
      header: "Reports",
      accessor: "reports",
      render: (row) => (
        <Button onClick={() => navigate(`/reports/${row.id}`)}>
          View Reports
        </Button>
      ),
    },
  ];

  const handleSubmit = () => {
    if (password === ADMIN_PASSWORD) {
      setIsAuthorized(true);
      onClose();
    } else {
      toast({
        title: "Access Denied",
        description: "Incorrect password.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    if (!isAuthorized) {
      onOpen();
    }
  }, [isAuthorized, onOpen]);

  useEffect(() => {
    fetchMessagesEnabledSetting();
  }, []);

  useEffect(() => {
    if (!activeJob || activeJob.completed_at) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const backendUrl = getBackendURL();
        const res = await fetch(`${backendUrl}/api/admin/bulk-job/${activeJob.job_id}/`);
        if (res.status === 404) {
          if (!cancelled) setActiveJob(null);
          return;
        }
        const job = await res.json();
        if (cancelled) return;
        setActiveJob(job);
        if (job.blocked && job.block_email) showBlockEmail(job.block_email);
        if (job.completed_at) {
          fetchSignupUsers();
        }
      } catch (err) {
        console.warn("[signups-dashboard] poll failed:", err);
      }
    };
    tick();
    const id = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(id); };
    // Deps intentionally narrow: we only want this effect to (re)spin up the
    // interval when a new job starts (job_id changes) or when the current job
    // completes (completed_at flips). Including the whole `activeJob` object
    // would tear down and recreate the interval on every poll tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJob?.job_id, activeJob?.completed_at]);

  if (!isAuthenticated) {
    return (
      <Modal
        isOpen={isPasswordOpen}
        onClose={() => navigate("/login")}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Enter Password</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Password</FormLabel>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={handlePasswordSubmit}>
              Submit
            </Button>
            <Button variant="ghost" onClick={() => navigate("/login")}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  if (!isAuthorized) {
    return (
      <Modal isOpen={isOpen} onClose={() => navigate("/")} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Protected Page</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Enter Password</FormLabel>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                placeholder="Password"
                autoFocus
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={handleSubmit}>
              Submit
            </Button>
            <Button variant="ghost" onClick={() => navigate("/")}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  // Protected content
  return (
    <Center flexDirection="column" className="schedules-container">
      <Flex align="center" mb={4}>
        <Text mx="auto" fontSize="2xl" fontWeight="bold">
          Super Admin Page - Scheduling and tracking of appointments
        </Text>
      </Flex>

      {/* Toolbar: one dropdown per category (components/admin/ActionMenu).
          Writes are disabled while impersonating; the hint says why. */}
      <Flex wrap="wrap" gap={{ base: 1, sm: 2 }} my={4} justify="center" align="center">
        <ActionMenu
          label="Patients"
          colorScheme="teal"
          sections={[
            {
              items: [
                {
                  label: "Add Patient",
                  isDisabled: isImpersonating(),
                  hint: isImpersonating() ? READ_ONLY_HINT : undefined,
                  onClick: () => setIsAddPatientModalOpen(true),
                },
                {
                  label: "Edit Patient",
                  isDisabled: !selectedPatient?.id || isImpersonating(),
                  hint: isImpersonating() ? READ_ONLY_HINT : undefined,
                  onClick: () => setIsEditPatientModalOpen(true),
                },
                {
                  label: "Send Forms",
                  isDisabled: !selectedPatient?.id || isImpersonating(),
                  hint: isImpersonating() ? READ_ONLY_HINT : undefined,
                  onClick: onSendFormOpen,
                },
                {
                  label: "Upload Files",
                  isDisabled: !selectedPatient?.id || isImpersonating(),
                  hint: isImpersonating() ? READ_ONLY_HINT : undefined,
                  onClick: () =>
                    navigate(`/upload-files/${selectedPatient?.id}`),
                },
                {
                  label: "View Reports",
                  isDisabled: !selectedPatient?.id,
                  onClick: () => navigate(`/reports/${selectedPatient?.id}`),
                },
              ],
            },
            {
              items: [
                {
                  label: "Patients Table",
                  onClick: () => setIsPatientTableConfirmOpen(true),
                },
                { label: "Settings", onClick: handleOpenSettings },
              ],
            },
          ]}
        />
        <ActionMenu
          label="Clinic setup"
          colorScheme="green"
          sections={[
            {
              title: "Schemas",
              items: [
                {
                  label: "Create Schema",
                  isDisabled: isImpersonating(),
                  hint: isImpersonating() ? READ_ONLY_HINT : undefined,
                  onClick: openCreateSchema,
                },
                { label: "Schema groups", onClick: onSchemaGroupsOpen },
                { label: "Fax Numbers", onClick: openFaxNumbers },
              ],
            },
            {
              title: "Scheduling & forms",
              items: [
                {
                  label: "Meeting Availability",
                  onClick: () => setIsMeetingAvailabilityOpen(true),
                },
                { label: "Version Timing", onClick: onVersionTimingOpen },
                {
                  label: "Order Form Templates",
                  onClick: onOrderTemplatesOpen,
                },
              ],
            },
          ]}
        />
        <ActionMenu
          label="Accounts & Billing"
          colorScheme="red"
          sections={[
            {
              items: [
                { label: "New Signups", onClick: openSignupsDashboard },
                {
                  label: "Credit Management",
                  isDisabled: isImpersonating(),
                  hint: isImpersonating() ? READ_ONLY_HINT : undefined,
                  onClick: onCreditManagementOpen,
                },
              ],
            },
          ]}
        />
        {/* HIPPAA is available to everyone the page admits (admin and doctor),
            matching rls_views._RLS_ADMIN_ROLES. Not gated on isImpersonating():
            the dashboard is read-only until Fix is used, and
            rls_fix_apply_view rejects view-as sessions server-side via
            @reject_if_view_as. Live Activity's endpoint enforces the same
            admin gate as the analytics tab. */}
        <ActionMenu
          label="Compliance & Monitoring"
          colorScheme="pink"
          sections={[
            {
              items: [
                { label: "HIPPAA", onClick: onHipaaOpen },
                {
                  label: "Compliance settings",
                  onClick: onComplianceSettingsOpen,
                },
                { label: "Live Activity", onClick: onLiveActivityOpen },
                // What texting costs, from Twilio's bill and its record of every text
                // (SmsSpendModal); the endpoint uses the same admin gate as Live Activity.
                { label: "SMS Spend", onClick: onSmsSpendOpen },
                // What clinic agents couldn't do, in every clinic (AgentGapsModal): for
                // everyone the page admits.
                { label: "Agent gaps", onClick: onAgentGapsOpen },
                // What agents may do for each clinic's people: clinic defaults and per-person
                // overrides (AgentPermissionsModal; moved here from the Admin page 2026-10-27).
                { label: "Agent permissions", onClick: onAgentPermsOpen },
              ],
            },
          ]}
        />
        {/* E2E Tests dispatches the Browserbase Playwright suite's GitHub
            workflow (separate repo; backend/api/e2e_tests.py). The modal
            disables Run while impersonating; the backend rejects view-as
            sessions too. */}
        <ActionMenu
          label="System"
          colorScheme="orange"
          sections={[
            {
              title: "Data",
              items: [
                { label: "Migration", onClick: onMigrationOpen },
                { label: "Storage", onClick: onStorageOpen },
                {
                  label: "Audio backup settings",
                  onClick: onAudioBackupSettingsOpen,
                },
                // Every recording + transcript on one schedule day, per schema.
                { label: "Audio Manager", onClick: onAudioManagerOpen },
              ],
            },
            {
              title: "Testing",
              items: [{ label: "E2E Tests", onClick: onE2EOpen }],
            },
          ]}
        />
        {/* Messages Master Switch — blocks all outbound SMS for supabaseSchema.
            Left on the toolbar so a blocked state is never hidden in a menu. */}
        <Tooltip
          label={
            messagesEnabled
              ? `Messages enabled - outbound SMS will send normally for ${supabaseSchema || "public"}`
              : `Messages disabled - all outbound SMS is blocked for ${supabaseSchema || "public"}`
          }
          placement="top"
        >
          <Box
            display="flex"
            alignItems="center"
            bg={messagesEnabled ? "blue.50" : "red.50"}
            px={3}
            py={1}
            borderRadius="md"
            borderWidth="1px"
            borderColor={messagesEnabled ? "blue.200" : "red.300"}
          >
            <Text
              fontSize={{ base: "10px", sm: "xs", md: "sm" }}
              fontWeight="bold"
              color={messagesEnabled ? "blue.700" : "red.700"}
            >
              Messages
            </Text>
            <Switch
              size="sm"
              colorScheme="blue"
              isChecked={messagesEnabled}
              onChange={toggleMessagesEnabledSetting}
              isDisabled={loadingMessagesEnabledSetting}
              ml={2}
            />
          </Box>
        </Tooltip>
      </Flex>

      <SchedulesTable
        showCheckboxes
        columns={columns}
        data={patients}
        onSelectedRowChange={handleSelectedRowChange}
        setSchedules={setPatients}
        isAdminPage={true} // Added isAdminPage prop to disable "See Patient" button and patient clickability
      />

      <AddPatientModal
        isOpen={isAddPatientModalOpen}
        onClose={() => setIsAddPatientModalOpen(false)}
        onAddPatient={async (newPatient) => {
          setPatients([...patients, newPatient]);
          setSelectedPatient(newPatient);
          setEmail(newPatient.email || "");
          setPhone(newPatient.tel_num || "");
          setIsAddPatientModalOpen(false);
          await handleFormSending(newPatient, toast);
        }}
      />

      <EditPatientModal
        isOpen={isEditPatientModalOpen}
        onClose={() => setIsEditPatientModalOpen(false)}
        patientData={selectedPatient}
        onUpdatePatient={(updated) =>
          setPatients(patients.map((p) => (p.id === updated.id ? updated : p)))
        }
      />

      <EditPatientModal
        isOpen={isDuplicateModalOpen}
        onClose={() => setIsDuplicateModalOpen(false)}
        patientData={selectedPatient}
        onUpdatePatient={handleDuplicateSave}
        isDuplicate
      />

      <FormSelectionModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        patientId={selectedPatient?.id}
      />

      <CreateSchemaModal
        isOpen={isCreateSchemaOpen}
        onClose={onCreateSchemaClose}
        onSubmit={handleCreateSchema}
      />

      <Modal
        isOpen={!!schemaTargetUser}
        onClose={() => setSchemaTargetUser(null)}
        isCentered
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit access</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Box fontSize="sm">
                <Text fontWeight="medium">{schemaTargetUser?.name || "—"}</Text>
                <Text color="gray.500">{schemaTargetUser?.email}</Text>
                <HStack mt={2} spacing={2} fontSize="xs" color="gray.600">
                  <Text>Current:</Text>
                  {getRoleBadge(schemaTargetUser?.role)}
                  <Text>· {schemaTargetUser?.schema || "no schema"}</Text>
                </HStack>
              </Box>

              <FormControl>
                <FormLabel fontSize="sm">Native schema</FormLabel>
                <Select
                  placeholder="Pick an existing schema…"
                  value={knownSchemas.includes(accessDraft.nativeInput) ? accessDraft.nativeInput : ""}
                  onChange={(e) => setAccessNative(e.target.value)}
                  mb={2}
                >
                  {knownSchemas.map((sname) => (
                    <option key={sname} value={sname}>{sname}</option>
                  ))}
                </Select>
                <Input
                  size="sm"
                  placeholder="…or type a schema name"
                  value={accessDraft.nativeInput}
                  onChange={(e) => setAccessNative(e.target.value)}
                />
                {tenantSchemaMismatch(schemaTargetUser?.role, accessDraftSchemas[0]) && (
                  <Text fontSize="xs" color="orange.500" mt={1}>
                    {normalizeRole(schemaTargetUser?.role)} accounts normally live in the{" "}
                    {tenantSchemaMismatch(schemaTargetUser?.role, accessDraftSchemas[0])} schema.
                  </Text>
                )}
                <Text fontSize="xs" color="gray.500" mt={1}>
                  The schema they land in at login. "{accessCurrentSchemas[0] || "none"}" moves to the additional schemas.
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Additional schemas</FormLabel>
                <Wrap spacing={2} mb={2}>
                  {accessDraftSchemas.slice(1).map((sname) => (
                    <WrapItem key={sname}>
                      <Tag size="sm" colorScheme="blue" variant="subtle">
                        <TagLabel>{sname}</TagLabel>
                        <TagCloseButton onClick={() => removeAccessExtra(sname)} />
                      </Tag>
                    </WrapItem>
                  ))}
                  {accessDraftSchemas.length <= 1 && (
                    <WrapItem>
                      <Text fontSize="xs" color="gray.400">none</Text>
                    </WrapItem>
                  )}
                </Wrap>
                <HStack spacing={2}>
                  <Select
                    size="sm"
                    placeholder="Add existing…"
                    value=""
                    onChange={(e) => e.target.value && addAccessExtra(e.target.value)}
                  >
                    {knownSchemas
                      .filter((sname) => !accessDraftSchemas.includes(sname))
                      .map((sname) => (
                        <option key={sname} value={sname}>{sname}</option>
                      ))}
                  </Select>
                  <Input
                    size="sm"
                    placeholder="…or type"
                    value={accessDraft.extraInput}
                    onChange={(e) => setAccessDraft((d) => ({ ...d, extraInput: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addAccessExtra(accessDraft.extraInput);
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    isDisabled={!accessDraft.extraInput.trim()}
                    onClick={() => addAccessExtra(accessDraft.extraInput)}
                  >
                    Add
                  </Button>
                </HStack>
              </FormControl>

              <Text fontSize="xs" color="gray.600">
                Will save schemas:{" "}
                <Text as="span" fontWeight="semibold">
                  {accessDraftSchemas.join(", ") || "none"}
                </Text>
                . Their row is seeded in the native schema's users table. Change the role from
                the Status column.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setSchemaTargetUser(null)}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              isDisabled={!accessCanSave}
              isLoading={savingAccess}
              onClick={handleSaveUserAccess}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <MeetingAvailabilityModal
        isOpen={isMeetingAvailabilityOpen}
        onClose={() => setIsMeetingAvailabilityOpen(false)}
      />

      <Modal isOpen={isSettingsOpen} onClose={onSettingsClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit User Settings</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              {["ZOHO_EMAIL", "ZOHO_PASSWORD", "TWILIO_PHONE_NUMBER"].map(
                (key) => (
                  <FormControl key={key}>
                    <FormLabel>{key}</FormLabel>
                    <Input
                      value={userSettings[key] || ""}
                      onChange={(e) =>
                        setUserSettings((prev) => ({
                          ...prev,
                          [key]: e.target.value,
                        }))
                      }
                    />
                  </FormControl>
                ),
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={saveUserSettings}>
              Save
            </Button>
            <Button variant="ghost" onClick={onSettingsClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isFaxNumbersOpen} onClose={onFaxNumbersClose} size="5xl" isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Schema Fax Numbers</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={3}>
              Set the fax number for each schema (blank = system default). SRFax
              credentials are required to receive faxes for non-default schemas;
              the password is write-only — leave it blank to keep the existing one.
            </Text>
            {faxNumbersLoading ? (
              <Spinner />
            ) : faxSchemas.length === 0 ? (
              <Text color="gray.500">No schemas found.</Text>
            ) : (
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Schema</Th>
                    <Th>Fax Number</Th>
                    <Th>SRFax Access ID</Th>
                    <Th>SRFax Password</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {faxSchemas.map((row) => (
                    <Tr key={row.schema_name}>
                      <Td fontWeight="medium">{row.schema_name}</Td>
                      <Td>
                        <Input
                          size="sm"
                          placeholder="Uses default number"
                          value={row.fax_num}
                          onChange={(e) => updateFaxNumberField(row.schema_name, e.target.value)}
                        />
                      </Td>
                      <Td>
                        <Input
                          size="sm"
                          placeholder="SRFax access id"
                          value={row.access_id}
                          onChange={(e) => updateCredField(row.schema_name, "access_id", e.target.value)}
                        />
                      </Td>
                      <Td>
                        <Input
                          size="sm"
                          type="password"
                          placeholder={row.has_password ? "•••••••• (configured)" : "not set"}
                          value={row.new_password}
                          onChange={(e) => updateCredField(row.schema_name, "new_password", e.target.value)}
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              isLoading={savingAllFax}
              onClick={saveAllFaxSettings}
              isDisabled={faxNumbersLoading || faxSchemas.length === 0}
            >
              Save All
            </Button>
            <Button variant="ghost" onClick={onFaxNumbersClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isSendFormOpen} onClose={onSendFormClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Send Form</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel>Email Address</FormLabel>
              <VStack align="flex-start">
                <Checkbox
                  isChecked={contactMethods.email}
                  onChange={(e) =>
                    setContactMethods((prev) => ({
                      ...prev,
                      email: e.target.checked,
                    }))
                  }
                >
                  Email
                </Checkbox>
                {contactMethods.email && (
                  <Input
                    placeholder="Enter email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    type="email"
                  />
                )}
                <Checkbox
                  isChecked={contactMethods.phone}
                  onChange={(e) =>
                    setContactMethods((prev) => ({
                      ...prev,
                      phone: e.target.checked,
                    }))
                  }
                >
                  Phone
                </Checkbox>
                {contactMethods.phone && (
                  <Input
                    placeholder="Enter phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    type="tel"
                  />
                )}
              </VStack>
            </FormControl>
            <FormControl>
              <FormLabel>Form Type</FormLabel>
              <VStack align="flex-start">
                {/* Show NP/NS for non-FF patients, FFS/FFP for FF patients */}
                {selectedPatient &&
                findPatientField(selectedPatient, "type")
                  ?.toUpperCase()
                  .includes("FF") ? (
                  <>
                    <Checkbox
                      isChecked={formType.includes(
                        findPatientField(
                          selectedPatient,
                          "type",
                        )?.toUpperCase() || "FFS",
                      )}
                      onChange={handleFormTypeChange(
                        findPatientField(
                          selectedPatient,
                          "type",
                        )?.toUpperCase() || "FFS",
                      )}
                    >
                      {findPatientField(
                        selectedPatient,
                        "type",
                      )?.toUpperCase() || "FFS"}{" "}
                      (Follow-up Form)
                    </Checkbox>
                  </>
                ) : (
                  <>
                    <Checkbox
                      isChecked={formType.includes("NP")}
                      onChange={handleFormTypeChange("NP")}
                    >
                      NP (New Pulmonary Patient)
                    </Checkbox>
                    <Checkbox
                      isChecked={formType.includes("NS")}
                      onChange={handleFormTypeChange("NS")}
                    >
                      NS (New Sleep Patient)
                    </Checkbox>
                  </>
                )}
              </VStack>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={sendForms}
              isLoading={isSendingForms}
              loadingText="Sending..."
            >
              Send
            </Button>
            <Button variant="ghost" onClick={onSendFormClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Version Timing Modal */}
      <Modal
        isOpen={isVersionTimingOpen}
        onClose={onVersionTimingClose}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Appointment Version Timing</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <AppointmentVersionSettings onClose={onVersionTimingClose} />
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isAudioBackupSettingsOpen}
        onClose={onAudioBackupSettingsClose}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Audio periodic backup (by schema)</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <AudioPeriodicBackupAdminSettings
              isOpen={isAudioBackupSettingsOpen}
              onClose={onAudioBackupSettingsClose}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isComplianceSettingsOpen}
        onClose={onComplianceSettingsClose}
        size={{ base: "full", sm: "lg" }}
        scrollBehavior="inside"
        isCentered
      >
        <ModalOverlay />
        <ModalContent
          maxH={{ base: "100dvh", sm: "90vh" }}
          maxW={{ base: "100vw", sm: "lg" }}
          m={{ base: 0, sm: 4 }}
          display="flex"
          flexDirection="column"
        >
          <ModalHeader flexShrink={0} fontSize={{ base: "md", md: "lg" }} pr={12}>
            Compliance monitoring (by schema)
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            pb={4}
            flex="1"
            minH={0}
            overflow="hidden"
            display="flex"
            flexDirection="column"
          >
            <ComplianceAdminSettings
              isOpen={isComplianceSettingsOpen}
              onClose={onComplianceSettingsClose}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Schema groups — link the schemas of doctors in the same clinic, so a
          patient approved into a group lands on every member's roster. */}
      <Modal
        isOpen={isSchemaGroupsOpen}
        onClose={onSchemaGroupsClose}
        size={{ base: "full", sm: "lg" }}
        scrollBehavior="inside"
        isCentered
      >
        <ModalOverlay />
        <ModalContent
          maxH={{ base: "100dvh", sm: "90vh" }}
          maxW={{ base: "100vw", sm: "lg" }}
          m={{ base: 0, sm: 4 }}
          display="flex"
          flexDirection="column"
        >
          <ModalHeader flexShrink={0} fontSize={{ base: "md", md: "lg" }} pr={12}>
            Schema groups (Schema: Group)
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            pb={4}
            flex="1"
            minH={0}
            overflow="hidden"
            display="flex"
            flexDirection="column"
          >
            <SchemaGroupAdminSettings
              isOpen={isSchemaGroupsOpen}
              onClose={() => {
                onSchemaGroupsClose();
                // The signups dropdown is built from this map — reload it so a
                // group created here is selectable without a page refresh.
                loadSchemaGroups();
              }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      <OrderTemplatesModal
        isOpen={isOrderTemplatesOpen}
        onClose={onOrderTemplatesClose}
      />

      {/* Credit Management Modal */}
      <Modal
        isOpen={isCreditManagementOpen}
        onClose={onCreditManagementClose}
        size="6xl"
      >
        <ModalOverlay />
        <ModalContent maxW="90vw" maxH="90vh">
          <ModalHeader>Credit Management</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflow="auto">
            <Box>
              <Tabs variant="enclosed">
                <TabList>
                  <Tab>Credit Limits</Tab>
                  <Tab>Transcription Logs</Tab>
                  <Tab>Saved Transcript Credits</Tab>
                  <Tab>Backup Path Migration</Tab>
                </TabList>

                <TabPanels>
                  <TabPanel>
                    <CreditLimitManager />
                  </TabPanel>

                  <TabPanel>
                    <TranscriptionLogs />
                  </TabPanel>

                  <TabPanel>
                    <SavedTranscriptCredits />
                  </TabPanel>

                  <TabPanel>
                    <BackupPathMigration />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* New Signups Dashboard Modal */}
      <Modal isOpen={isSignupsDashboardOpen} onClose={onSignupsDashboardClose} size="full">
        <ModalOverlay />
        <ModalContent maxH="95vh" minH="85vh" maxW="min(95vw, 1800px)" my="2.5vh">
          <ModalHeader>
            <HStack justify="space-between" pr={8}>
              <Text>New Signups Dashboard</Text>
              <HStack spacing={2}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleExportSignupsCSV}
                  isDisabled={selectedSignupIds.size === 0}
                  title={
                    selectedSignupIds.size === 0
                      ? "Select one or more rows below, then export"
                      : undefined
                  }
                >
                  Export CSV ({selectedSignupIds.size})
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => { await refreshAnalyticsData(); fetchSignupUsers(); }}
                  isLoading={signupsLoading}
                >
                  Refresh
                </Button>
                <VStack align="stretch" spacing={0}>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="orange"
                    onClick={() => fetchZohoBlockStatus({ force: true, announce: true })}
                    isLoading={zohoCheckLoading}
                  >
                    Recheck Zoho status
                  </Button>
                  {lastBlockEmail?.received_at && (
                    <Text fontSize="xs" color="gray.500" mt={1} textAlign="right">
                      Last block email: {formatBlockEmailTimestamp(lastBlockEmail)}
                    </Text>
                  )}
                </VStack>
              </HStack>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody overflow="auto">
            <Tabs variant="enclosed" index={signupsActiveTab} onChange={setSignupsActiveTab}>
              <TabList>
                <Tab>Signups</Tab>
                <Tab>Referral Stats</Tab>
                <Tab>User Analytics</Tab>
                <Tab>Patient Signups</Tab>
                <Tab>User Access</Tab>
              </TabList>
              <TabPanels>

                {/* ── Tab 1: Signups list ── */}
                <TabPanel px={0}>
                  {activeJob && (
                    <BulkJobProgressStrip job={activeJob} onDismiss={dismissActiveJob} />
                  )}
                  <VStack align="stretch" spacing={2} mb={4}>
                    <HStack spacing={2}>
                      {[
                        { key: "today", label: "Today" },
                        { key: "week", label: "Last 7 days" },
                        { key: "month", label: "Last 30 days" },
                        { key: "all", label: "All time" },
                      ].map(({ key, label }) => (
                        <Button
                          key={key}
                          size="sm"
                          colorScheme={signupPeriod === key ? "pink" : "gray"}
                          variant={signupPeriod === key ? "solid" : "outline"}
                          onClick={() => setSignupPeriod(key)}
                        >
                          {label}
                        </Button>
                      ))}
                    </HStack>
                    <HStack spacing={2} flexWrap="wrap">
                      {[
                        { key: "all", label: "All" },
                        { key: "pending", label: "Pending" },
                        { key: "accepted", label: "Accepted" },
                      ].map(({ key, label }) => (
                        <Button
                          key={key}
                          size="xs"
                          colorScheme={signupStatus === key ? "orange" : "gray"}
                          variant={signupStatus === key ? "solid" : "outline"}
                          onClick={() => setSignupStatus(key)}
                        >
                          {label}
                        </Button>
                      ))}
                      <HStack spacing={2} pl={1}>
                        <Text fontSize="xs" color="gray.600">Role:</Text>
                        <Select
                          size="xs"
                          width="auto"
                          value={signupRoleFilter}
                          onChange={(e) => setSignupRoleFilter(e.target.value)}
                        >
                          <option value="all">All roles</option>
                          {signupAvailableRoles.map((role) => (
                            <option key={role} value={role}>{role}</option>
                          ))}
                        </Select>
                      </HStack>
                    </HStack>
                  </VStack>

                  {signupsLoading ? (
                    <Flex justify="center" py={8}><Spinner /></Flex>
                  ) : (() => {
                    const filtered = getFilteredSignups();
                    const filteredIds = filtered.map((u) => u.id);
                    const allFilteredSelected =
                      filteredIds.length > 0 && filteredIds.every((id) => selectedSignupIds.has(id));
                    const someFilteredSelected =
                      !allFilteredSelected && filteredIds.some((id) => selectedSignupIds.has(id));
                    const selectedCount = selectedSignupIds.size;
                    const welcomeNeededCount = [...selectedSignupIds].filter((id) => {
                      const u = signupUsers.find((x) => x.id === id);
                      return u && !u.signup_emailed_at;
                    }).length;
                    const approvablePendingCount = [...selectedSignupIds].filter((id) => {
                      const u = signupUsers.find((x) => x.id === id);
                      return u && isPendingApprovable(u.role);
                    }).length;
                    return (
                      <>
                        <Flex
                          justify="space-between"
                          align="center"
                          mb={3}
                          flexWrap="wrap"
                          gap={2}
                        >
                          <Text fontSize="sm" color="gray.500">
                            {filtered.length} user{filtered.length !== 1 ? "s" : ""}
                            {selectedCount > 0 && ` · ${selectedCount} selected`}
                          </Text>
                          <HStack spacing={2}>
                            {selectedCount > 0 && (
                              <>
                                <Button
                                  size="xs"
                                  colorScheme="blue"
                                  isDisabled={welcomeNeededCount === 0}
                                  isLoading={bulkActionLoading === "welcome"}
                                  onClick={handleBulkSendWelcome}
                                >
                                  Send welcome to {welcomeNeededCount}
                                </Button>
                                <Button
                                  size="xs"
                                  colorScheme="green"
                                  isDisabled={approvablePendingCount === 0}
                                  isLoading={bulkActionLoading === "approval"}
                                  onClick={handleBulkApprove}
                                >
                                  Approve {approvablePendingCount} pending
                                </Button>
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  onClick={clearSignupSelection}
                                >
                                  Clear
                                </Button>
                              </>
                            )}
                          </HStack>
                        </Flex>
                        <Box overflowX="auto">
                          <Table size="sm" variant="simple">
                            <Thead>
                              <Tr>
                                <Th width="40px" px={2}>
                                  <Checkbox
                                    isChecked={allFilteredSelected}
                                    isIndeterminate={someFilteredSelected}
                                    isDisabled={filteredIds.length === 0}
                                    onChange={toggleSelectAllFiltered}
                                  />
                                </Th>
                                <Th>Name</Th>
                                <Th>Email</Th>
                                <Th>Signed Up</Th>
                                <Th>Referred By</Th>
                                <Th>Use Case</Th>
                                <Th>Status</Th>
                                <Th>Email Status</Th>
                                <Th></Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {filtered.length === 0 ? (
                                <Tr><Td colSpan={9} textAlign="center" color="gray.400" py={6}>No signups in this period</Td></Tr>
                              ) : (
                                filtered.map((u) => {
                                  const { reason, referralSource, referralDetail } = parseSignupReason(u.signup_reason);
                                  const referralDisplay = referralDetail
                                    ? `${referralDetail} (${referralSource})`
                                    : referralSource || "—";
                                  const approvableRole = getApprovedRole(u.role);
                                  return (
                                    <Tr key={u.id} bg={selectedSignupIds.has(u.id) ? "blue.50" : undefined}>
                                      <Td px={2}>
                                        <Checkbox
                                          isChecked={selectedSignupIds.has(u.id)}
                                          onChange={() => toggleSignupSelected(u.id)}
                                        />
                                      </Td>
                                      <Td fontWeight="medium">{u.name || "—"}</Td>
                                      <Td fontSize="xs">{u.email}</Td>
                                      <Td fontSize="xs" whiteSpace="nowrap">
                                        {u.created_at ? formatSignupTime(u.created_at, viewerTimezone) : "—"}
                                      </Td>
                                      <Td fontSize="xs">{referralDisplay}</Td>
                                      <Td fontSize="xs" maxW="180px" isTruncated title={reason}>{reason || "—"}</Td>
                                      <Td>
                                        <HStack spacing={1}>
                                          <Select
                                            size="xs"
                                            maxW="140px"
                                            value={normalizeRole(u.role) || ""}
                                            isDisabled={roleSavingUserId === u.id}
                                            onChange={(e) => handleChangeUserRole(u, e.target.value)}
                                            title={`Role: ${u.role || "none"}`}
                                          >
                                            {!roleOptions.includes(normalizeRole(u.role) || "") && (
                                              <option value={normalizeRole(u.role) || ""} disabled>
                                                {normalizeRole(u.role) || "unknown"}
                                              </option>
                                            )}
                                            {roleOptions.map((r) => (
                                              <option key={r} value={r}>{r}</option>
                                            ))}
                                          </Select>
                                          {roleSavingUserId === u.id ? (
                                            <Spinner size="xs" />
                                          ) : (
                                            isPending(u.role) && <Badge colorScheme="yellow">pending</Badge>
                                          )}
                                        </HStack>
                                      </Td>
                                      <Td>{getEmailStatusCell(u)}</Td>
                                      <Td>
                                        <HStack spacing={2}>
                                          {isPendingApprovable(u.role) && (
                                            <Button
                                              size="xs"
                                              colorScheme="green"
                                              isLoading={approvingUserId === u.id}
                                              onClick={() => handleApproveUser(u.id)}
                                            >
                                              Accept as {approvableRole}
                                            </Button>
                                          )}
                                          <Tooltip
                                            label={`Schemas: ${u.schema || "none"}`}
                                            placement="left"
                                          >
                                            <Button
                                              size="xs"
                                              variant="outline"
                                              colorScheme="blue"
                                              onClick={() => openAccessModal(u)}
                                            >
                                              Edit access
                                            </Button>
                                          </Tooltip>
                                        </HStack>
                                      </Td>
                                    </Tr>
                                  );
                                })
                              )}
                            </Tbody>
                          </Table>
                        </Box>
                        <UserListBulkAddBar
                          selectedUserIds={selectedSignupIds}
                          onClearSelection={clearSignupSelection}
                        />
                      </>
                    );
                  })()}
                </TabPanel>

                {/* ── Tab 2: Referral Stats ── */}
                <TabPanel px={0}>
                  {signupsLoading ? (
                    <Flex justify="center" py={8}><Spinner /></Flex>
                  ) : (() => {
                    const { matched, unmatched } = getReferralStats();
                    const referralTable = (rows, rankOffset = 0) => (
                      <Table size="sm" variant="simple">
                        <Thead>
                          <Tr>
                            <Th>#</Th>
                            <Th>Referrer</Th>
                            <Th isNumeric>Referred</Th>
                            <Th>Who They Referred</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {rows.map(({ referrer, count, users }, idx) => (
                            <Tr key={referrer}>
                              <Td color="gray.400" fontWeight="bold">{rankOffset + idx + 1}</Td>
                              <Td fontWeight="semibold">{referrer}</Td>
                              <Td isNumeric>
                                <Badge colorScheme={count >= 5 ? "green" : count >= 2 ? "blue" : "gray"} fontSize="sm">
                                  {count}
                                </Badge>
                              </Td>
                              <Td fontSize="xs" color="gray.600">{users.join(", ")}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    );

                    return (
                      <VStack align="stretch" spacing={6}>
                        {matched.length > 0 ? (
                          <Box>{referralTable(matched)}</Box>
                        ) : (
                          <Text color="gray.400" py={4} textAlign="center">No referral link signups yet</Text>
                        )}

                        {unmatched.length > 0 && (
                          <Box>
                            <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={2}>
                              Other / Unverified ({unmatched.length})
                            </Text>
                            {referralTable(unmatched)}
                          </Box>
                        )}
                      </VStack>
                    );
                  })()}
                </TabPanel>

                {/* ── Tab 3: User Analytics ── */}
                <TabPanel px={0}>
                  <UserAnalyticsTab
                    refreshNonce={analyticsRefreshNonce}
                    initialUserId={analyticsInitialUserId}
                    initialTimeWindow={analyticsInitialTimeWindow}
                  />
                </TabPanel>

                {/* ── Tab 4: Patient Signups ── */}
                <TabPanel px={0}>
                  {signupsLoading ? (
                    <Flex justify="center" py={8}><Spinner /></Flex>
                  ) : (() => {
                    const counts = getPatientSignupCounts();
                    const rows = getPatientSignupRows();
                    return (
                      <VStack align="stretch" spacing={4}>
                        {/* Plain-English "why is signup broken on this server"
                            report — missing env vars, unapplied migrations,
                            dead mail/SMS tokens, stale schema cache, etc. */}
                        <Box borderWidth={1} borderRadius="md" p={3}>
                          <HStack justify="space-between" align="center">
                            <Box>
                              <Text fontWeight="semibold" fontSize="sm">Signup health check</Text>
                              <Text fontSize="xs" color="gray.500">
                                Explains in plain English why verification fails on prod but works on dev.
                              </Text>
                            </Box>
                            <Button
                              size="xs"
                              colorScheme="purple"
                              variant="outline"
                              isLoading={signupHealthLoading}
                              loadingText="Checking…"
                              onClick={runPatientSignupHealthCheck}
                            >
                              Run health check
                            </Button>
                          </HStack>
                          {signupHealth && (
                            <VStack align="stretch" spacing={1} mt={3}>
                              <Text
                                fontSize="sm"
                                fontWeight="medium"
                                color={signupHealth.ok ? "green.600" : "red.600"}
                              >
                                {signupHealth.summary}
                              </Text>
                              {(signupHealth.checks || []).map((check, i) => {
                                const color =
                                  check.status === "error" ? "red.600"
                                    : check.status === "warn" ? "orange.500" : "green.600";
                                const mark =
                                  check.status === "error" ? "✗"
                                    : check.status === "warn" ? "!" : "✓";
                                return (
                                  <Box key={i} fontSize="xs" mt={1}>
                                    <Text as="span" fontWeight="bold" color={color}>
                                      {mark} {check.name}:
                                    </Text>{" "}
                                    <Text as="span" color="gray.700">{check.message}</Text>
                                    {check.fix && check.status !== "ok" && (
                                      <Text color="gray.500" pl={4} mt="1px">→ Fix: {check.fix}</Text>
                                    )}
                                  </Box>
                                );
                              })}
                            </VStack>
                          )}
                        </Box>
                        <HStack spacing={3} flexWrap="wrap">
                          <Box borderWidth={1} borderRadius="md" p={3} minW="180px">
                            <Text fontSize="xs" color="gray.500">Approved</Text>
                            <Text fontSize="2xl" fontWeight="bold" color="green.600">{counts.approved}</Text>
                          </Box>
                          <Box borderWidth={1} borderRadius="md" p={3} minW="180px">
                            <Text fontSize="xs" color="gray.500">Needs admin approval</Text>
                            <Text fontSize="2xl" fontWeight="bold" color="orange.600">{counts.needs_review}</Text>
                          </Box>
                        </HStack>
                        <HStack spacing={2} flexWrap="wrap">
                          {[
                            { key: "needs_review", label: "Needs review" },
                            { key: "approved", label: "Approved" },
                            { key: "denied", label: "Denied" },
                            { key: "all", label: "All" },
                          ].map(({ key, label }) => (
                            <Button
                              key={key}
                              size="sm"
                              colorScheme={patientSignupStatusFilter === key ? "teal" : "gray"}
                              variant={patientSignupStatusFilter === key ? "solid" : "outline"}
                              onClick={() => setPatientSignupStatusFilter(key)}
                            >
                              {label}
                            </Button>
                          ))}
                        </HStack>
                        {rows.length === 0 ? (
                          <Text color="gray.400" py={6} textAlign="center">No patient signups in this filter</Text>
                        ) : (
                          rows.map((u) => (
                            // One card per patient so it's clear the approve/deny
                            // buttons belong to the patient shown directly above.
                            <Box
                              key={u.id}
                              borderWidth={2}
                              borderColor="gray.300"
                              borderRadius="lg"
                              overflow="hidden"
                            >
                              {renderPatientComparison(u)}
                              {renderPatientApprovalControls(u)}
                            </Box>
                          ))
                        )}
                      </VStack>
                    );
                  })()}
                </TabPanel>

                {/* ── Tab 5: User Access — search any user, edit their schema list ── */}
                <TabPanel px={0}>
                  <UserAccessTab
                    knownSchemas={accessTabSchemas}
                    renderRoleBadge={getRoleBadge}
                    onSave={handleSaveSearchedUserSchemas}
                    onSetSuperadmin={handleSetSearchedUserSuperadmin}
                  />
                </TabPanel>

              </TabPanels>
            </Tabs>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onSignupsDashboardClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <ZohoBlockModal blockEmail={blockEmail} onClose={dismissBlockEmail} />
      <Modal
        isOpen={isPatientTableConfirmOpen}
        onClose={closePatientTableConfirm}
        isCentered
        size="xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        {/* On the View-table tab the modal becomes a fixed, screen-sized panel
            (98vw × 90vh) whose body → tabs → panel form a flex column, so the table
            box is the ONLY scroll region (no competing outer scrollbar, and shift+
            wheel horizontal scroll stays reliable). The Build tab is a normal dialog. */}
        <ModalContent
          maxW={patientTableTab === 1 ? "98vw" : undefined}
          h={patientTableTab === 1 ? "90vh" : undefined}
        >
          <ModalHeader>Patient tables — {supabaseSchema || "public"}</ModalHeader>
          <ModalCloseButton isDisabled={isBuildingPatientTable} />
          <ModalBody
            display={patientTableTab === 1 ? "flex" : undefined}
            flexDirection="column"
            minH={0}
            overflow={patientTableTab === 1 ? "hidden" : undefined}
          >
            <Tabs
              variant="enclosed"
              isLazy
              index={patientTableTab}
              onChange={setPatientTableTab}
              display={patientTableTab === 1 ? "flex" : undefined}
              flexDirection="column"
              flex={patientTableTab === 1 ? "1" : undefined}
              minH={0}
            >
              <TabList>
                <Tab>Build / import</Tab>
                <Tab>View table</Tab>
              </TabList>
              <TabPanels
                flex={patientTableTab === 1 ? "1" : undefined}
                minH={0}
                display={patientTableTab === 1 ? "flex" : undefined}
                flexDirection="column"
              >
                <TabPanel px={0}>
                  <VStack align="stretch" spacing={3}>
                    <Text>{PATIENT_TABLE_CONFIRMATION.body}</Text>
                    <Text fontSize="sm" color="gray.600">
                      Current schema: {supabaseSchema || "public"}
                    </Text>
                    <Checkbox
                      colorScheme="red"
                      isChecked={dropAndRecreate}
                      onChange={(e) => setDropAndRecreate(e.target.checked)}
                      isDisabled={isBuildingPatientTable}
                    >
                      <Text fontSize="sm">Drop all existing rows and recreate from scratch</Text>
                    </Checkbox>
                    {dropAndRecreate && (
                      <Text fontSize="sm" color="red.600" fontWeight="semibold">
                        ⚠️ This permanently deletes every patient row in{" "}
                        {supabaseSchema || "public"} — including CSV imports and
                        patient-portal signups — then rebuilds from notes-c. This cannot be undone.
                      </Text>
                    )}
                    {isBuildingPatientTable && (
                      <HStack
                        spacing={3}
                        bg="blue.50"
                        borderWidth="1px"
                        borderColor="blue.100"
                        borderRadius="md"
                        px={3}
                        py={2}
                      >
                        <Spinner size="sm" color="blue.500" />
                        <Text fontSize="sm" color="blue.800">
                          {PATIENT_TABLE_PROGRESS_MESSAGES[patientTableProgressIndex]}
                        </Text>
                      </HStack>
                    )}
                  </VStack>
                </TabPanel>
                <TabPanel px={0} flex="1" minH={0} display="flex" flexDirection="column">
                  <PatientTableViewer schema={supabaseSchema || "public"} fill />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
          <ModalFooter flexWrap="wrap" gap={3}>
            {patientTableTab === 0 ? (
              <>
                <Button
                  variant="outline"
                  colorScheme="green"
                  mr="auto"
                  flexShrink={0}
                  whiteSpace="nowrap"
                  onClick={() => { closePatientTableConfirm(); setIsPatientImportOpen(true); }}
                  isDisabled={isBuildingPatientTable}
                >
                  Import from CSV / Excel
                </Button>
                <Button
                  variant="outline"
                  colorScheme="teal"
                  flexShrink={0}
                  whiteSpace="nowrap"
                  onClick={() => handleBuildPatientTable(true)}
                  isDisabled={isBuildingPatientTable || dropAndRecreate}
                >
                  {PATIENT_TABLE_EMPTY_CONFIRMATION.confirmLabel}
                </Button>
                <Button
                  variant="ghost"
                  flexShrink={0}
                  whiteSpace="nowrap"
                  onClick={closePatientTableConfirm}
                  isDisabled={isBuildingPatientTable}
                >
                  {PATIENT_TABLE_CONFIRMATION.cancelLabel}
                </Button>
                <Button
                  colorScheme={dropAndRecreate ? "red" : "blue"}
                  flexShrink={0}
                  whiteSpace="nowrap"
                  onClick={() => handleBuildPatientTable(false)}
                  isLoading={isBuildingPatientTable}
                  loadingText="Building"
                  isDisabled={isBuildingPatientTable}
                >
                  {dropAndRecreate ? "Drop & rebuild" : PATIENT_TABLE_CONFIRMATION.confirmLabel}
                </Button>
              </>
            ) : (
              <Button variant="ghost" onClick={closePatientTableConfirm}>
                Close
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
      <PatientImportModal
        isOpen={isPatientImportOpen}
        onClose={() => setIsPatientImportOpen(false)}
      />
      <HipaaComplianceModal isOpen={isHipaaOpen} onClose={onHipaaClose} />

      <LiveActivityModal isOpen={isLiveActivityOpen} onClose={onLiveActivityClose} />
      <SmsSpendModal isOpen={isSmsSpendOpen} onClose={onSmsSpendClose} />
      <AgentGapsModal isOpen={isAgentGapsOpen} onClose={onAgentGapsClose} />
      <AgentPermissionsModal isOpen={isAgentPermsOpen} onClose={onAgentPermsClose} />
      <MigrationModal isOpen={isMigrationOpen} onClose={onMigrationClose} onRun={handleRunMigration} />
      <StorageModal isOpen={isStorageOpen} onClose={onStorageClose} />
      <E2ETestsModal isOpen={isE2EOpen} onClose={onE2EClose} />
      <AudioDayManagerModal isOpen={isAudioManagerOpen} onClose={onAudioManagerClose} />

    </Center>
  );
};

export default SuperAdmin;
